import { useState, useEffect, useMemo, useRef } from 'react';
import { Modal } from '../common/Modal';
import { CopyLinkButton } from '../common/CopyLinkButton';
import { buildShareUrl } from '../../services/shareLinks';
import { Button } from '../common/Button';
import {
  getCustomCalibres,
  getCustomCalibreById,
  saveCustomCalibre,
  checkCalibreConflict,
  getReferenceCountForCalibre,
  deleteCustomCalibre,
  uploadCalibreImage,
  deleteCalibreImage,
  fetchImageBlob,
  getImageUrl,
  buildCalibreDocId,
  enrichCalibreWithAI,
  getWatchBrands,
  getLookupCollection,
  CALIBRE_IMAGE_ATTRIBUTION_SOURCES,
  CALIBRE_LEVEL_ATTRIBUTION_SOURCES,
  resolveDefaultCalibreSource,
  getCalibreFamilyById,
  getManufacturerTierNamesContext,
  mutateManufacturerTierVocabulary,
  mutateFamilyTierVocabulary,
  onTierVocabularyChanged,
  type CustomCalibreEntry,
  type CalibreAliasDetail,
  type CalibreData,
  type CalibreImage,
  type CalibreTier,
  type AIProposedCalibreTier,
} from '../../services/firebase';
import {
  duplicateAliasMessage,
  findDuplicateAlias,
  normalizeCalibreKey,
} from '../../services/calibreResolution';
import {
  akaBrandScopeIds,
  defaultAkaBrandId,
} from '../../services/calibreClassification';
import { MAX_CALIBRE_IMAGES } from '../../services/calibreImages';
import { labelForCalibreSource } from '../../services/calibreSourceLabels';
import type { WatchBrand, LookupValue, DocsLink } from '../../types';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';
import { LookupFieldSelect } from './LookupFieldSelect';
import { LookupMultiSelect } from './LookupMultiSelect';
import { parseMultiLookupValues } from '../../utils/multiLookupValues';
import { TagInput } from './TagInput';
import {
  AiCalibreEnrichPanel,
  aiSpecsToCalibreData,
  resolveBrandManufacturerId,
  planProposedTierMerge,
  type ProposedTierMergeChoice,
  resultToDraft,
  type AiEnrichState,
} from './AiCalibreEnrichPanel';
import { CalibreActionToolbar, isGenericMovementName } from './CalibreActionToolbar';
import {
  siblingWarningVerdict,
  SIBLING_SIGNAL_LABELS,
} from './calibreSiblingSignals';
import { CalibreFamilyField } from './CalibreFamilyField';
import { CalibreRevisionsCard } from './CalibreRevisionsCard';
import { DocsLinkChip } from '../common/DocsLinkChip';
import { ReapplyCalibreToReferences } from './ReapplyCalibreToReferences';
import { reapplyBlockedReason } from './reapplyCalibre';
import { calibreWasRenamed, withOldNameAlias } from './calibreRenameAlias';
import { useDocsLinks } from '../../hooks/useDocsLinks';
import { CalibreTiersEditor, type TierMediaProps } from './CalibreTiersEditor';
import { ReclassifyCalibreWizard } from './ReclassifyCalibreWizard';
import {
  sanitizeTierForWrite,
  formatAccuracy,
  tiersForBrand,
  mergeableTierFields,
  type MergeableTierField,
} from './calibreTiers';
import { MovementManufacturerQuickCreate } from '../MovementManufacturers';
import type { TierVocabularyEntry } from '@watchkeeper/shared-types';
import {
  newTierEntryId,
  tierNameOptions,
  upsertTierEntry,
  type CalibreTierVocabulary,
} from '../../utils/tierVocabulary';
import { vocabRenameTarget } from './calibreTierVocabMatch';
import type { CalibreCreateSeed } from './adapters/extractionToCalibres';
import toast from 'react-hot-toast';

interface CustomCalibreModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBrandName?: string;
  /**
   * Brand-context doc id to pre-fill the calibre's brandId. Set this when the
   * modal is launched from a brand-detail screen, a brand's ref/calibre list,
   * or anywhere a single brand is the implicit subject.
   */
  defaultBrandId?: string;
  /**
   * Movement-manufacturer FK to pre-fill alongside {@link defaultBrandId}, for
   * the callers that can resolve the brand's ONE linked manufacturer without
   * guessing (see `soleManufacturerIdForBrand`). Leave unset when the answer is
   * ambiguous — the field then stays empty for the admin to pick, which is the
   * whole point of the caller-side check.
   */
  defaultManufacturerId?: string;
  /** When set, opens the modal directly into the form for this entry (skipping the list). */
  initialEditEntry?: CustomCalibreEntry | null;
  /** When 'create', opens the modal directly into an empty create form. */
  initialMode?: 'list' | 'create';
  /**
   * Seed values when opening directly in create mode — used by the Brand Portal
   * approve flow (pre-fill from a request payload) and by the bulk-import
   * calibre step (pre-fill from the extractor's scraped calibre page). Only
   * applied on open. Numeric fields are strings because FormState holds raw
   * input text; the caller does any unit parsing.
   */
  initialCreateValues?: CalibreCreateSeed;
  /** Contextual note shown at the top of the create form (e.g. brand-owner request notes). */
  requestNote?: string;
  /**
   * Called after a successful save so the parent can refresh its list. Receives
   * the saved calibre doc id (the Brand Portal approve flow uses this as the
   * resolved entity id).
   */
  onSaved?: (savedId: string) => void;
}

const MAX_IMAGES = MAX_CALIBRE_IMAGES;

/** Which dropdown is being filled — the vocabularies differ. */
export type SourceScope = 'calibre' | 'image';

/**
 * The options for one source dropdown.
 *
 * Two vocabularies, not one. A CALIBRE is attributed to the `brand` (in-house)
 * or the `manufacturer` (third-party); an IMAGE can additionally be
 * `watchkeeper`, meaning a photograph we own rather than one the brand or the
 * maker supplied. `watchkeeper` is deliberately absent from the calibre-level
 * list: we do not make movements, so it would invite an attribution that cannot
 * be true. New entries default via {@link resolveDefaultCalibreSource}, which
 * answers the calibre-level question and so never returns it.
 *
 * Other provenance values (`manual`, `calibrecorner`, `user_submitted`) remain
 * valid but are set by import/upload flows rather than chosen here. Both scopes
 * still append the CURRENT value when it is one of those, so editing an entry
 * never silently rewrites a source nobody touched — which is also what keeps a
 * calibre already carrying a legacy tag editable after this split.
 */

export function sourceOptionsFor(scope: SourceScope, current?: string): string[] {
  const base = [
    ...(scope === 'image'
      ? CALIBRE_IMAGE_ATTRIBUTION_SOURCES
      : CALIBRE_LEVEL_ATTRIBUTION_SOURCES),
  ] as string[];
  if (current && !base.includes(current)) base.push(current);
  return base;
}

function emptyForm() {
  return {
    name: '',
    brandId: '',
    brandName: '',
    manufacturerId: '',
    additionalManufacturerIds: [] as string[],
    originalManufacturerId: '',
    description: '',
    aliasDetails: [] as CalibreAliasDetail[],
    movementType: '',
    frequencyVph: '',
    jewels: '',
    powerReserveHours: '',
    positionsAdjusted: '',
    accuracyLowerSecPerDay: '',
    accuracyUpperSecPerDay: '',
    accuracySymmetric: false,
    diameterMm: '',
    heightMm: '',
    liftAngleDegrees: '',
    serviceIntervalYears: '',
    casingDiameterMm: '',
    baseMovement: '',
    windingDirection: '',
    antiShockSystem: '',
    rotorType: '',
    regulatorSystem: '',
    countryOfManufacture: '',
    manualWindingDirection: '',
    hairspring: '',
    lignes: '',
    hacking: false,
    manualWinding: false,
    quickSetDate: false,
    isCosc: false,
    isGlashuetteRegulation: false,
    isChronometer: false,
    isGenevaSeal: false,
    isGrandSeikoStandard: false,
    isMETAS: false,
    isGMT: false,
    isChronograph: false,
    isInHouse: false,
    isFamily: false,
    isCatchAll: false,
    columnWheel: false,
    doubleBarrel: false,
    escapement: '',
    batteryType: '',
    batteryLifeYears: '',
    shockResistanceG: '',
    antiMagneticAm: '',
    antiMagneticGauss: '',
    date: [] as string[],
    hands: [] as string[],
    chronograph: [] as string[],
    additionals: [] as string[],
    astronomical: [] as string[],
    acoustic: [] as string[],
    // Decoration & finishing (v1.39.0)
    balanceType: [] as string[],
    movementArchitecture: '',
    // Auto winding architecture (v1.45.0) — only surfaced when the selected
    // movement type carries the `isAuto` property.
    windingArchitecture: '',
    numberOfBridges: '',
    barrelCount: '',
    // Materials (v1.92.0)
    rotorMaterial: '',
    bridgeMaterial: '',
    caseMaterialsAvailable: [] as string[],
    // '' means "unset" for the skeletonization enum.
    skeletonization: '' as '' | 'full' | 'partial' | 'none',
    movementFinishing: '',
    plateFinishing: '',
    decorations: [] as string[],
    rhodiumPlated: false,
    giltFinish: false,
    // Display name for the synthetic "Base" tier row; '' → falls back to "Base".
    baseTierName: '',
  };
}

type FormState = ReturnType<typeof emptyForm>;

/**
 * Lenient brand renderer. Reads brandId first; falls back to legacy brandName.
 *   - brandId resolves to a known brand → friendly chip
 *   - brandId set but unknown → amber legacy badge with raw id
 *   - only brandName set (pre-backfill) → amber legacy badge with raw name
 *   - neither → italic "Global"
 * Auditable via the backfill / audit panels.
 */
function CalibreBrandCell({
  entry,
  brandById,
}: {
  entry: { brandId?: string; brandName?: string };
  brandById: Map<string, string>;
}) {
  if (entry.brandId) {
    const resolved = brandById.get(entry.brandId);
    if (resolved) {
      return (
        <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-50 text-brand-800 border border-brand-200'>
          {resolved}
        </span>
      );
    }
    return (
      <span
        className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200'
        title='brandId set but does not match any watchBrand doc'
      >
        legacy: {entry.brandId}
      </span>
    );
  }
  if (entry.brandName) {
    return (
      <span
        className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200'
        title='Legacy free-text brand value — not yet mapped to a watchBrand doc'
      >
        legacy: {entry.brandName}
      </span>
    );
  }
  return <span className='italic text-gray-400'>Global</span>;
}

function entryToForm(entry: CustomCalibreEntry): FormState {
  const d = entry.data;
  const aliasDetails: CalibreAliasDetail[] = entry.aliasDetails && entry.aliasDetails.length > 0
    ? entry.aliasDetails
    : (entry.alsoKnownAs || []).map((name) => ({ name, showInExplore: false }));
  return {
    name: entry.name,
    brandId: entry.brandId || '',
    brandName: entry.brandName || '',
    manufacturerId: entry.manufacturerId || '',
    additionalManufacturerIds: entry.additionalManufacturerIds || [],
    originalManufacturerId: entry.originalManufacturerId || '',
    description: entry.description || '',
    aliasDetails,
    movementType: d.movementType || '',
    frequencyVph: d.frequencyVph?.toString() || '',
    jewels: d.jewels?.toString() || '',
    powerReserveHours: d.powerReserveHours?.toString() || '',
    positionsAdjusted: d.positionsAdjusted?.toString() || '',
    // Accuracy bounds are signed and can be 0 or negative — compare against
    // undefined/null (not truthiness) so 0 renders instead of an empty field.
    accuracyLowerSecPerDay:
      d.accuracyLowerSecPerDay != null ? d.accuracyLowerSecPerDay.toString() : '',
    accuracyUpperSecPerDay:
      d.accuracyUpperSecPerDay != null ? d.accuracyUpperSecPerDay.toString() : '',
    accuracySymmetric: d.accuracySymmetric || false,
    diameterMm: d.diameterMm?.toString() || '',
    heightMm: d.heightMm?.toString() || '',
    liftAngleDegrees: d.liftAngleDegrees?.toString() || '',
    serviceIntervalYears: d.serviceIntervalYears?.toString() || '',
    casingDiameterMm: d.casingDiameterMm?.toString() || '',
    baseMovement: d.baseMovement || '',
    windingDirection: d.windingDirection || '',
    antiShockSystem: d.antiShockSystem || '',
    rotorType: d.rotorType || '',
    regulatorSystem: d.regulatorSystem || '',
    countryOfManufacture: d.countryOfManufacture || '',
    manualWindingDirection: d.manualWindingDirection || '',
    hairspring: d.hairspring || '',
    lignes: d.lignes || '',
    hacking: d.hacking || false,
    manualWinding: d.manualWinding || false,
    quickSetDate: d.quickSetDate || false,
    isCosc: d.isCosc || false,
    isGlashuetteRegulation: d.isGlashuetteRegulation || false,
    isChronometer: d.isChronometer || false,
    isGenevaSeal: d.isGenevaSeal || false,
    isGrandSeikoStandard: d.isGrandSeikoStandard || false,
    isMETAS: d.isMETAS || false,
    isGMT: d.isGMT || false,
    isChronograph: d.isChronograph || false,
    isInHouse: d.isInHouse || false,
    isFamily: d.isFamily || false,
    isCatchAll: d.isCatchAll || false,
    columnWheel: d.columnWheel || false,
    doubleBarrel: d.doubleBarrel || false,
    escapement: d.escapement || '',
    batteryType: d.batteryType || '',
    batteryLifeYears: d.batteryLifeYears?.toString() || '',
    shockResistanceG: d.shockResistanceG?.toString() || '',
    antiMagneticAm: d.antiMagneticAm?.toString() || '',
    antiMagneticGauss: d.antiMagneticGauss?.toString() || '',
    date: d.date || [],
    hands: d.hands || [],
    chronograph: d.chronograph || [],
    additionals: d.additionals || [],
    astronomical: d.astronomical || [],
    acoustic: d.acoustic || [],
    // Decoration & finishing (v1.39.0). balanceType went multi-select in
    // v1.91.0; a doc written before migrateCalibreBalanceType ran still
    // holds the bare scalar, so coerce rather than trust the type.
    balanceType: parseMultiLookupValues(d.balanceType),
    movementArchitecture: d.movementArchitecture || '',
    windingArchitecture: d.windingArchitecture || '',
    numberOfBridges: d.numberOfBridges?.toString() || '',
    barrelCount: d.barrelCount?.toString() || '',
    // Materials (v1.92.0). caseMaterialsAvailable has only ever been an array,
    // so it needs no scalar coercion the way balanceType does above.
    rotorMaterial: d.rotorMaterial || '',
    bridgeMaterial: d.bridgeMaterial || '',
    caseMaterialsAvailable: d.caseMaterialsAvailable || [],
    skeletonization: d.skeletonization || '',
    movementFinishing: d.movementFinishing || '',
    plateFinishing: d.plateFinishing || '',
    decorations: d.decorations || [],
    rhodiumPlated: d.rhodiumPlated || false,
    giltFinish: d.giltFinish || false,
    baseTierName: d.baseTierName || '',
  };
}

function formToData(form: FormState): CalibreData {
  return {
    movementType: form.movementType || undefined,
    frequencyVph: form.frequencyVph ? parseInt(form.frequencyVph) : undefined,
    jewels: form.jewels ? parseInt(form.jewels) : undefined,
    powerReserveHours: form.powerReserveHours ? parseInt(form.powerReserveHours) : undefined,
    positionsAdjusted: form.positionsAdjusted ? parseInt(form.positionsAdjusted) : undefined,
    // Signed: use `!== ''` (not truthiness) so a legitimate 0 or negative value
    // is written rather than dropped.
    accuracyLowerSecPerDay:
      form.accuracyLowerSecPerDay !== '' ? parseFloat(form.accuracyLowerSecPerDay) : undefined,
    accuracyUpperSecPerDay:
      form.accuracyUpperSecPerDay !== '' ? parseFloat(form.accuracyUpperSecPerDay) : undefined,
    // Unset flag == false == asymmetric (the default); omit rather than persist
    // false, matching the sibling booleans.
    accuracySymmetric: form.accuracySymmetric || undefined,
    diameterMm: form.diameterMm ? parseFloat(form.diameterMm) : undefined,
    heightMm: form.heightMm ? parseFloat(form.heightMm) : undefined,
    liftAngleDegrees: form.liftAngleDegrees ? parseFloat(form.liftAngleDegrees) : undefined,
    serviceIntervalYears: form.serviceIntervalYears ? parseInt(form.serviceIntervalYears) : undefined,
    casingDiameterMm: form.casingDiameterMm ? parseFloat(form.casingDiameterMm) : undefined,
    baseMovement: form.baseMovement || undefined,
    windingDirection: form.windingDirection || undefined,
    antiShockSystem: form.antiShockSystem || undefined,
    rotorType: form.rotorType || undefined,
    regulatorSystem: form.regulatorSystem || undefined,
    countryOfManufacture: form.countryOfManufacture || undefined,
    manualWindingDirection: form.manualWindingDirection || undefined,
    hairspring: form.hairspring || undefined,
    lignes: form.lignes || undefined,
    hacking: form.hacking || undefined,
    manualWinding: form.manualWinding || undefined,
    quickSetDate: form.quickSetDate || undefined,
    isCosc: Boolean(form.isCosc),
    isGlashuetteRegulation: Boolean(form.isGlashuetteRegulation),
    isChronometer: Boolean(form.isChronometer),
    isGenevaSeal: Boolean(form.isGenevaSeal),
    isGrandSeikoStandard: Boolean(form.isGrandSeikoStandard),
    isMETAS: Boolean(form.isMETAS),
    isGMT: Boolean(form.isGMT),
    isChronograph: Boolean(form.isChronograph),
    isInHouse: Boolean(form.isInHouse),
    isFamily: Boolean(form.isFamily),
    isCatchAll: Boolean(form.isCatchAll),
    columnWheel: form.columnWheel || undefined,
    doubleBarrel: form.doubleBarrel || undefined,
    escapement: form.escapement || undefined,
    batteryType: form.batteryType || undefined,
    batteryLifeYears: form.batteryLifeYears ? parseFloat(form.batteryLifeYears) : undefined,
    shockResistanceG: form.shockResistanceG ? parseInt(form.shockResistanceG) : undefined,
    antiMagneticAm: form.antiMagneticAm ? parseInt(form.antiMagneticAm) : undefined,
    antiMagneticGauss: form.antiMagneticGauss ? parseInt(form.antiMagneticGauss) : undefined,
    date: form.date.length > 0 ? form.date : undefined,
    hands: form.hands.length > 0 ? form.hands : undefined,
    chronograph: form.chronograph.length > 0 ? form.chronograph : undefined,
    additionals: form.additionals.length > 0 ? form.additionals : undefined,
    astronomical: form.astronomical.length > 0 ? form.astronomical : undefined,
    acoustic: form.acoustic.length > 0 ? form.acoustic : undefined,
    // Decoration & finishing (v1.39.0). Enum: '' means unset → omit. Booleans
    // omit when false, matching the sibling optional booleans above.
    balanceType: form.balanceType.length > 0 ? form.balanceType : undefined,
    movementArchitecture: form.movementArchitecture || undefined,
    // Not cleared when the movement type flips away from automatic — same
    // behaviour as the gated battery fields above; the value simply stops
    // being rendered.
    windingArchitecture: form.windingArchitecture || undefined,
    numberOfBridges: form.numberOfBridges ? parseInt(form.numberOfBridges) : undefined,
    barrelCount: form.barrelCount ? parseInt(form.barrelCount) : undefined,
    // Materials (v1.92.0). Same '' → omit / empty-array → omit convention as
    // every sibling above: an emptied picker clears the field rather than
    // writing a blank.
    rotorMaterial: form.rotorMaterial || undefined,
    bridgeMaterial: form.bridgeMaterial || undefined,
    caseMaterialsAvailable:
      form.caseMaterialsAvailable.length > 0 ? form.caseMaterialsAvailable : undefined,
    skeletonization: form.skeletonization || undefined,
    movementFinishing: form.movementFinishing || undefined,
    plateFinishing: form.plateFinishing || undefined,
    decorations: form.decorations.length > 0 ? form.decorations : undefined,
    rhodiumPlated: form.rhodiumPlated || undefined,
    giltFinish: form.giltFinish || undefined,
    // Display-only label for the synthetic base row; omit empty strings so no
    // blank value is written to Firestore (falls back to "Base" on display).
    baseTierName: form.baseTierName.trim() || undefined,
  };
}

/**
 * One row in the "Apply AI suggestions" review: a form field the AI proposed a
 * value for, with the current value, the proposed value, and whether they differ.
 * `key` is the selection key — the form field name for specs, or the `__name__`
 * / `__description__` sentinels handled specially on apply.
 */
interface AiFieldDiff {
  key: string;
  label: string;
  current: string;
  proposed: string;
  changed: boolean;
}

const NAME_KEY = '__name__';
const DESCRIPTION_KEY = '__description__';

/** Human-readable labels for the apply-review rows; falls back to a humanized key. */
const AI_FIELD_LABELS: Record<string, string> = {
  [NAME_KEY]: 'Canonical name',
  [DESCRIPTION_KEY]: 'Description',
  isFamily: 'Movement Family',
  isCatchAll: 'Catch-all Placeholder',
  movementType: 'Movement Type',
  frequencyVph: 'Frequency (VPH)',
  powerReserveHours: 'Power Reserve (h)',
  positionsAdjusted: 'Positions Adjusted',
  accuracyLowerSecPerDay: 'Accuracy Lower (s/day)',
  accuracyUpperSecPerDay: 'Accuracy Upper (s/day)',
  accuracySymmetric: 'Accuracy Symmetric (±)',
  diameterMm: 'Diameter (mm)',
  heightMm: 'Height (mm)',
  liftAngleDegrees: 'Lift Angle (°)',
  serviceIntervalYears: 'Service Interval (yrs)',
  casingDiameterMm: 'Casing Diameter (mm)',
  baseMovement: 'Base Movement',
  windingDirection: 'Winding Direction',
  antiShockSystem: 'Anti-Shock System',
  countryOfManufacture: 'Country of Manufacture',
  batteryLifeYears: 'Battery Life (yrs)',
  shockResistanceG: 'Shock Resistance (G)',
  antiMagneticAm: 'Anti-Magnetic (A/m)',
  antiMagneticGauss: 'Anti-Magnetic (Gauss)',
  balanceType: 'Balance Type',
  movementArchitecture: 'Architecture',
  windingArchitecture: 'Auto Winding Architecture',
  numberOfBridges: 'Bridges',
  barrelCount: 'Barrels',
  rotorMaterial: 'Rotor Material',
  bridgeMaterial: 'Bridge Material',
  caseMaterialsAvailable: 'Case Materials Available',
  skeletonization: 'Skeletonization',
  movementFinishing: 'Movement Finishing',
  plateFinishing: 'Plate Finishing',
  decorations: 'Decorations',
  rhodiumPlated: 'Rhodium Plated',
  giltFinish: 'Gilt Finish',
};

function humanizeAiKey(key: string): string {
  if (AI_FIELD_LABELS[key]) return AI_FIELD_LABELS[key];
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

/** Render any form/spec value as a short display string for the review table. */
function aiDisplayValue(v: unknown): string {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

export function CustomCalibreModal({
  isOpen,
  onClose,
  currentBrandName,
  defaultBrandId,
  defaultManufacturerId,
  initialEditEntry,
  initialMode,
  initialCreateValues,
  requestNote,
  onSaved,
}: CustomCalibreModalProps) {
  const directEditMode = !!initialEditEntry || initialMode === 'create';
  const [entries, setEntries] = useState<CustomCalibreEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // AKA/name collision warning. Set (instead of saving) when the calibre being
  // saved shares a name/alias with another calibre. Warn-only: no merge, no
  // delete — the user chooses "Save anyway" or "Go back". brand-scoped aliases
  // to different brands are NOT collisions and never populate this.
  const [conflictWarning, setConflictWarning] = useState<{
    akaName: string;
    otherCalibreName: string;
    otherCalibreId: string;
  } | null>(null);
  // Rename warning. Set (instead of saving) when an existing calibre with linked
  // references is renamed: the refs stay linked by calibreId but show the new
  // name. "Rename" saves; "Cancel" leaves the editor open with nothing written.
  const [renameWarning, setRenameWarning] = useState<{ refCount: number; newName: string } | null>(null);
  // AI enrichment flow. `aiState` is the async state (loading / result /
  // editable draft / error); `refInfo` is the reference-info free text (kept
  // separate so it survives re-runs); `showAiPanel` toggles the inline panel.
  const [aiState, setAiState] = useState<AiEnrichState>({});
  const [refInfo, setRefInfo] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(false);
  // Per-field "apply AI suggestions" review. `aiApplyOpen` toggles the dialog;
  // `aiFieldDiffs` is the current-vs-proposed diff snapshot taken when the
  // dialog opens; `aiFieldSel` tracks which fields the user has ticked to apply.
  const [aiApplyOpen, setAiApplyOpen] = useState(false);
  const [aiFieldDiffs, setAiFieldDiffs] = useState<AiFieldDiff[]>([]);
  const [aiFieldSel, setAiFieldSel] = useState<Record<string, boolean>>({});
  // AI-proposed tier variants shown in the apply review. Keyed by array index;
  // seeded to accept only tiers whose name doesn't already exist (append-only,
  // never clobbers a user-created tier). See mergeProposedTiers.
  const [aiTierSel, setAiTierSel] = useState<Record<number, boolean>>({});
  /**
   * Optional remap per proposed tier index → the id of an EXISTING tier its
   * overrides should merge into, instead of creating a tier under the AI's name.
   *
   * The reason this exists: the model routinely proposes the right grade under a
   * different name ("RR1102-C" for a tier the curator already calls "C"), and
   * the append-only merge could only create a duplicate or skip it. Absent (or
   * '') means "create new", which stays the default — a remap is always a
   * deliberate act.
   */
  const [aiTierRemap, setAiTierRemap] = useState<Record<number, string>>({});
  /**
   * Proposal index → the listed name to rename its remap target to. Seeded when
   * a proposal is the listed spelling of a grade an existing tier holds under
   * another spelling ("Elaboré" for an existing "Special (Elaboré)").
   */
  const [aiTierRename, setAiTierRename] = useState<Record<number, string>>({});
  /**
   * What to do with a proposed tier whose name ALREADY exists on the calibre.
   * Unticked is Skip and stays the default; ticked, this picks between the two
   * ways to reach the existing tier:
   *
   *  - 'merge' — overlay the fields ticked in `aiTierMergeFields` onto the
   *    name-matched tier, OVERWRITING them. The only path where the model's
   *    answer beats the curator's, and only field by field.
   *  - 'remap' — send the proposal at a tier the curator chooses from the
   *    dropdown, fill-empty, exactly as a non-matching proposal can be remapped.
   *
   * Absent reads as 'merge', which is what a name match usually means.
   */
  const [aiTierMode, setAiTierMode] = useState<Record<number, 'merge' | 'remap'>>({});
  /**
   * Proposal index → the mergeable field keys ticked in its merge picker.
   * Seeded to every offered field (see `aiTierMergeRows`) the first time a row
   * is expanded, so the common "take all of it" case is one click.
   */
  const [aiTierMergeFields, setAiTierMergeFields] = useState<
    Record<number, Set<string>>
  >({});
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  // The full entry currently open in the form (existing edits only; null when
  // adding). Retained for its deprecation/redirect flags and as the source for
  // the reclassify wizard — `form` doesn't carry those fields.
  const [editingEntry, setEditingEntry] = useState<CustomCalibreEntry | null>(
    null,
  );
  // Open state for the "Reclassify as brand-specific tier" wizard.
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  // Current family membership of the calibre being edited. Tracked separately
  // from `form` because `familyId` lives on the calibre doc (not in FormState)
  // and is written immediately by the family field, independent of Save.
  const [currentFamilyId, setCurrentFamilyId] = useState<string | null>(null);
  // Canonical tier-name vocabulary of the calibre's family (empty when it has no
  // family or the family defines none). Drives the tier editor's combobox +
  // did-you-mean; resolved from the family doc whenever `currentFamilyId` changes.
  const [familyTierNames, setFamilyTierNames] = useState<string[]>([]);
  // The family's structured list + name, and the manufacturer's list + name.
  // Together with `familyTierNames` they are the calibre's tier vocabulary.
  const [familyVocab, setFamilyVocab] = useState<{ name?: string; entries: TierVocabularyEntry[] }>({ entries: [] });
  const [manufacturerVocab, setManufacturerVocab] = useState<{ name?: string; entries: TierVocabularyEntry[] }>({
    entries: [],
  });
  const [showForm, setShowForm] = useState(false);

  // Base movement navigation stack
  const [baseMovementInDb, setBaseMovementInDb] = useState<boolean | null>(null);
  const [baseMovementChecking, setBaseMovementChecking] = useState(false);
  const [returnToEntry, setReturnToEntry] = useState<CustomCalibreEntry | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [images, setImages] = useState<CalibreImage[]>([]);
  // Tier variants live inside CalibreData.tiers but are edited as their own
  // nested state (not part of the flat FormState) and merged back into `data` at
  // save time.
  const [tiers, setTiers] = useState<CalibreTier[]>([]);
  const [editingSource, setEditingSource] = useState<string | undefined>(undefined);
  const [editingSourceUrl, setEditingSourceUrl] = useState<string | undefined>(undefined);
  // Attribution default for new entries / images: 'brand' for an in-house
  // movement (brandId === manufacturerId), 'manufacturer' otherwise.
  const defaultCalibreSource = resolveDefaultCalibreSource(
    form.brandId,
    form.manufacturerId,
  );
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageCaption, setNewImageCaption] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [brandOptions, setBrandOptions] = useState<
    Pick<WatchBrand, 'id' | 'name' | 'docs'>[]
  >([]);
  const [newAliasName, setNewAliasName] = useState('');
  const PAGE_SIZE = 20;

  // Resolve the family's canonical tier vocabulary whenever the calibre's family
  // membership changes. No family → empty vocab (tier names stay free-text).
  useEffect(() => {
    if (!currentFamilyId) {
      setFamilyTierNames([]);
      setFamilyVocab({ entries: [] });
      return;
    }
    let cancelled = false;
    getCalibreFamilyById(currentFamilyId)
      .then((fam) => {
        if (cancelled) return;
        setFamilyTierNames(fam?.tierNames ?? []);
        setFamilyVocab({ name: fam?.name, entries: fam?.tierVocabulary ?? [] });
      })
      .catch((err) => {
        console.error('[CustomCalibre] resolve family tier vocab failed', err);
        if (!cancelled) {
          setFamilyTierNames([]);
          setFamilyVocab({ entries: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentFamilyId]);

  // The movement manufacturer's tier names — the base layer every calibre it
  // makes draws from, family or not.
  const tierManufacturerId = form.manufacturerId.trim();
  useEffect(() => {
    if (!tierManufacturerId) {
      setManufacturerVocab({ entries: [] });
      return;
    }
    let cancelled = false;
    getManufacturerTierNamesContext(tierManufacturerId)
      .then((ctx) => !cancelled && setManufacturerVocab(ctx))
      .catch((err) => {
        console.error('[CustomCalibre] resolve manufacturer tier vocab failed', err);
        if (!cancelled) setManufacturerVocab({ entries: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [tierManufacturerId]);

  // Writes made elsewhere in this tab (the manufacturer / family Tier Names
  // sections, or the add-as-new below) land here without a reload.
  useEffect(
    () =>
      onTierVocabularyChanged((kind, id, next) => {
        if (kind === 'manufacturer' && id === tierManufacturerId) setManufacturerVocab((v) => ({ ...v, entries: next }));
        if (kind === 'family' && id === currentFamilyId) setFamilyVocab((v) => ({ ...v, entries: next }));
      }),
    [tierManufacturerId, currentFamilyId],
  );

  const tierVocabulary = useMemo<CalibreTierVocabulary>(
    () => ({
      manufacturerName: manufacturerVocab.name,
      manufacturer: manufacturerVocab.entries,
      familyName: currentFamilyId ? familyVocab.name : undefined,
      family: familyVocab.entries,
      legacyFamilyNames: familyTierNames,
    }),
    [manufacturerVocab, familyVocab, familyTierNames, currentFamilyId],
  );

  // The tier editor's add-as-new: a GENERIC name, to the list the curator
  // picked. Each write is the list's own transaction (shared with the Tier
  // Names sections), and the change broadcast above refreshes this modal.
  const handleAddTierName = async (destination: 'manufacturer' | 'family', tierName: string) => {
    const entry: TierVocabularyEntry = { id: newTierEntryId(), canonicalName: tierName.trim(), akas: [] };
    if (destination === 'manufacturer') {
      if (!tierManufacturerId) throw new Error('Set the calibre’s manufacturer first.');
      await mutateManufacturerTierVocabulary(tierManufacturerId, (cur) => upsertTierEntry(cur, entry));
    } else {
      if (!currentFamilyId) throw new Error('This calibre has no family.');
      await mutateFamilyTierVocabulary(currentFamilyId, tierManufacturerId, (cur) => upsertTierEntry(cur, entry));
    }
  };

  useEffect(() => {
    if (!isOpen || brandOptions.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const brands = await getWatchBrands();
        if (cancelled) return;
        const sorted = brands
          // `docs` rides along because `getWatchBrands` returns whole documents
          // anyway — the docs chip beside the brand field costs no extra read.
          .map((b) => ({ id: b.id, name: b.name, docs: b.docs }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setBrandOptions(sorted);
      } catch (err) {
        console.warn('Failed to load brand list for alias editor:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, brandOptions.length]);

  // The Primary-manufacturer and Co-developed-with dropdowns. Both render a
  // label and write back an id, so both are served from
  // `watchkeeper_manufacturers` rather than by reading all 176
  // `movement_manufacturers` documents on every open of this modal — which, on
  // a screen the reference editor mounts unconditionally, is most of its opens.
  //
  // Through the shared hook so a manufacturer created in the quick-create modal
  // below — or anywhere else in the app — lands in these dropdowns without a
  // page reload. It keeps the once-per-open fetch; it just also listens.
  const { manufacturers: manufacturerCatalogue, loading: manufacturersLoading } =
    useManufacturerCatalogue({ enabled: isOpen });

  // The catalogue arrives sorted by canonical `name`; these dropdowns show the
  // display label, so they order by that instead — as they always have.
  const manufacturers = useMemo(
    () =>
      [...manufacturerCatalogue].sort((a, b) =>
        (a.displayName || a.name).localeCompare(b.displayName || b.name),
      ),
    [manufacturerCatalogue],
  );

  const manufacturerById = useMemo(
    () => new Map(manufacturerCatalogue.map((m) => [m.id, m])),
    [manufacturerCatalogue],
  );

  /**
   * Brands an alias on THIS calibre may be scoped to, or `null` for "no
   * narrowing — offer the whole catalogue". Driven by the calibre's manufacturer
   * set: an in-house maker (IWC, Kenissi) narrows the per-alias brand picker to
   * the brands it is actually associated with, because an IWC movement's aliases
   * are never a Seiko alias; a generic maker (ETA) or an unset manufacturer
   * narrows nothing, as before.
   *
   * `brandIdSet` supplies the "manufacturer doc id IS a brand slug" test the
   * helper takes — brandOptions is the same watchBrands list the picker renders,
   * so a scope can never name a brand this modal couldn't show.
   */
  const brandIdSet = useMemo(
    () => new Set(brandOptions.map((b) => b.id)),
    [brandOptions],
  );

  const aliasBrandScope = useMemo(
    () =>
      akaBrandScopeIds(
        {
          manufacturerId: form.manufacturerId,
          additionalManufacturerIds: form.additionalManufacturerIds,
        },
        manufacturerById,
        (id) => brandIdSet.has(id),
      ),
    [form.manufacturerId, form.additionalManufacturerIds, manufacturerById, brandIdSet],
  );

  /**
   * The alias picker's option list. Falls back to the full catalogue whenever
   * the scope resolves to nothing this modal can actually render — a narrowed
   * list of zero would be a picker the user cannot use, which is strictly worse
   * than the unfiltered list they had before.
   */
  const aliasBrandOptions = useMemo(() => {
    if (!aliasBrandScope) return brandOptions;
    const allowed = new Set(aliasBrandScope);
    const narrowed = brandOptions.filter((b) => allowed.has(b.id));
    return narrowed.length > 0 ? narrowed : brandOptions;
  }, [aliasBrandScope, brandOptions]);

  /**
   * The brand picker for a brand-specific TIER: the same list as the alias
   * picker (every brand for a generic maker like Sellita, only its own brands
   * for an in-house one — James, 2026-10-29), with the brands this calibre is
   * already curated for first: its own brand, then every alias's brand scope.
   */
  const tierBrandOptions = useMemo(() => {
    const first = new Set(
      [form.brandId, ...form.aliasDetails.map((a) => a.brandId)].filter((b): b is string => !!b && !!b.trim()),
    );
    if (first.size === 0) return aliasBrandOptions;
    return [...aliasBrandOptions.filter((b) => first.has(b.id)), ...aliasBrandOptions.filter((b) => !first.has(b.id))];
  }, [aliasBrandOptions, form.brandId, form.aliasDetails]);

  const [movementTypeLookupValues, setMovementTypeLookupValues] = useState<
    LookupValue[]
  >([]);
  // `isOpen`-guarded like every other fetch in this modal. Without the guard
  // this fired on every ReferenceEditor mount — the editor renders a closed
  // CustomCalibreModal unconditionally, and the component body has no early
  // return — making it the third read of `lookup_movement_types` on a screen
  // that never showed the modal.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getLookupCollection('lookup_movement_types')
      .then((v) => {
        if (!cancelled) setMovementTypeLookupValues(v);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const movementTypeNeedsBattery = useMemo(() => {
    if (!form.movementType || movementTypeLookupValues.length === 0) return false;
    const valueLower = form.movementType.toLowerCase();
    const match = movementTypeLookupValues.find(
      (lv) =>
        lv.value === form.movementType ||
        lv.displayName === form.movementType ||
        lv.value.toLowerCase() === valueLower ||
        lv.displayName.toLowerCase() === valueLower ||
        lv.synonyms?.some((s) => s.toLowerCase() === valueLower),
    );
    return match?.properties?.includes('needsBattery') ?? false;
  }, [form.movementType, movementTypeLookupValues]);

  // Mirrors movementTypeNeedsBattery above, but keyed on the `isAuto` property:
  // gates the Auto Winding Architecture field, which is only meaningful on
  // self-winding movements. James maintains the property in Lookup Manager, so
  // an unflagged movement type simply hides the field rather than erroring.
  const movementTypeIsAuto = useMemo(() => {
    if (!form.movementType || movementTypeLookupValues.length === 0) return false;
    const valueLower = form.movementType.toLowerCase();
    const match = movementTypeLookupValues.find(
      (lv) =>
        lv.value === form.movementType ||
        lv.displayName === form.movementType ||
        lv.value.toLowerCase() === valueLower ||
        lv.displayName.toLowerCase() === valueLower ||
        lv.synonyms?.some((s) => s.toLowerCase() === valueLower),
    );
    return match?.properties?.includes('isAuto') ?? false;
  }, [form.movementType, movementTypeLookupValues]);

  const handleAddAlias = () => {
    const name = newAliasName.trim();
    if (!name) return;
    // Reject a duplicate out loud rather than silently no-op'ing, and route it
    // through the shared check so diacritics and Caliber/Calibre spelling can't
    // slip a dupe past.
    const duplicate = findDuplicateAlias(
      { name: form.name, aliasDetails: form.aliasDetails },
      name,
    );
    if (duplicate) {
      toast.error(duplicateAliasMessage(duplicate, name, form.name.trim() || 'this calibre'));
      return;
    }
    // A new alias on an in-house calibre defaults to that manufacturer's brand
    // rather than to universal: an IWC-only movement's alternative names belong
    // to IWC, and leaving them universal is how a short, ambiguous alias ("52010")
    // ends up matching another brand's refs. Only filled when the answer is
    // unambiguous — generic makers, an unset manufacturer, and a multi-brand
    // maker with no primary all stay blank, as before. The per-alias selector
    // below is still how a user changes or clears it.
    const defaultBrand = defaultAkaBrandId(
      {
        manufacturerId: form.manufacturerId,
        additionalManufacturerIds: form.additionalManufacturerIds,
      },
      manufacturerById,
      (id) => brandIdSet.has(id),
    );
    setForm((prev) => ({
      ...prev,
      aliasDetails: [
        ...prev.aliasDetails,
        defaultBrand
          ? { name, brandId: defaultBrand, showInExplore: false }
          : { name, showInExplore: false },
      ],
    }));
    setNewAliasName('');
  };

  const handleUpdateAlias = (idx: number, patch: Partial<CalibreAliasDetail>) => {
    setForm((prev) => ({
      ...prev,
      aliasDetails: prev.aliasDetails.map((a, i) => {
        if (i !== idx) return a;
        // Drop keys patched to undefined (e.g. a cleared brandId) instead of
        // merging `undefined` in — Firestore rejects undefined field values.
        const merged: Record<string, unknown> = { ...a };
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined) delete merged[k];
          else merged[k] = v;
        }
        return merged as unknown as CalibreAliasDetail;
      }),
    }));
  };

  const handleRemoveAlias = (idx: number) => {
    setForm((prev) => ({
      ...prev,
      aliasDetails: prev.aliasDetails.filter((_, i) => i !== idx),
    }));
  };

  const loadEntries = async (fromServer = false) => {
    setLoading(true);
    try {
      const data = await getCustomCalibres(fromServer);
      setEntries(data);
    } catch (error) {
      console.error('Failed to load custom calibres:', error);
      toast.error('Failed to load custom calibres');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const toResolve = images
      .map((img) => img.storagePath)
      .filter((p): p is string => !!p && !(p in resolvedUrls));
    if (toResolve.length === 0) return;
    (async () => {
      const entries = await Promise.all(
        toResolve.map(async (path) => {
          try {
            const url = await getImageUrl(path);
            return [path, url] as const;
          } catch {
            return [path, ''] as const;
          }
        }),
      );
      if (cancelled) return;
      setResolvedUrls((prev) => {
        const next = { ...prev };
        for (const [path, url] of entries) {
          if (url) next[path] = url;
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [images, resolvedUrls]);

  useEffect(() => {
    if (!isOpen) return;
    if (directEditMode) {
      // Skip loading the list; open straight into the form.
      if (initialEditEntry) {
        setForm(entryToForm(initialEditEntry));
        setImages(initialEditEntry.images || initialEditEntry.data?.images || []);
        setTiers(initialEditEntry.data?.tiers ?? []);
        setEditingSource(initialEditEntry.source);
        setEditingSourceUrl(initialEditEntry.sourceUrl);
        setEditingId(initialEditEntry.id);
        setEditingEntry(initialEditEntry);
        setCurrentFamilyId(initialEditEntry.familyId ?? null);
      } else {
        const seeded = emptyForm();
        if (defaultBrandId) {
          seeded.brandId = defaultBrandId;
          if (currentBrandName) seeded.brandName = currentBrandName;
        }
        // Independent of defaultBrandId: the caller decides whether the brand's
        // manufacturer is knowable, and only passes one when it is.
        if (defaultManufacturerId) seeded.manufacturerId = defaultManufacturerId;
        // Pre-fill from a Brand Portal request payload (approve flow) or an
        // extractor's scraped calibre page (bulk-import calibre step). Only the
        // fields that map cleanly onto the form are seeded; anything the caller
        // couldn't parse is absent and the field stays empty. Everything else
        // (manufacturer free-text, brand-owner notes) is surfaced via requestNote.
        const seed = initialCreateValues;
        if (seed?.name) seeded.name = seed.name;
        if (seed?.movementType) seeded.movementType = seed.movementType;
        if (seed?.description) seeded.description = seed.description;
        if (seed?.frequencyVph) seeded.frequencyVph = seed.frequencyVph;
        if (seed?.jewels) seeded.jewels = seed.jewels;
        if (seed?.powerReserveHours) seeded.powerReserveHours = seed.powerReserveHours;
        if (seed?.diameterMm) seeded.diameterMm = seed.diameterMm;
        if (seed?.heightMm) seeded.heightMm = seed.heightMm;
        if (seed?.lignes) seeded.lignes = seed.lignes;
        if (seed?.baseMovement) seeded.baseMovement = seed.baseMovement;
        if (seed?.isChronograph) seeded.isChronograph = true;
        if (seed?.date?.length) seeded.date = seed.date;
        if (seed?.hands?.length) seeded.hands = seed.hands;
        if (seed?.chronograph?.length) seeded.chronograph = seed.chronograph;
        if (seed?.additionals?.length) seeded.additionals = seed.additionals;
        setForm(seeded);
        setImages([]);
        setTiers([]);
        setEditingSource(undefined);
        setEditingSourceUrl(seed?.sourceUrl || undefined);
        setEditingId(null);
        setEditingEntry(null);
        setCurrentFamilyId(null);
      }
      // Clear any AI panel state carried over from a previous modal session —
      // the component stays mounted across open/close, so stale state can leak.
      setShowAiPanel(false);
      setAiState({});
      setRefInfo('');
      setShowForm(true);
    } else {
      loadEntries();
    }
    // initialCreateValues is captured at open time only — intentionally omitted
    // from deps so a new object literal each render doesn't clobber edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    directEditMode,
    initialEditEntry,
    defaultBrandId,
    defaultManufacturerId,
    currentBrandName,
  ]);

  const handleAdd = () => {
    const seeded = emptyForm();
    if (defaultBrandId) {
      seeded.brandId = defaultBrandId;
      if (currentBrandName) seeded.brandName = currentBrandName;
    }
    setForm(seeded);
    setImages([]);
    setTiers([]);
    setEditingSource(undefined);
    setEditingSourceUrl(undefined);
    setEditingId(null);
    setEditingEntry(null);
    setCurrentFamilyId(null);
    resetAiPanel();
    setShowForm(true);
  };

  const handleEdit = (entry: CustomCalibreEntry) => {
    setForm(entryToForm(entry));
    setImages(entry.images || entry.data?.images || []);
    setTiers(entry.data?.tiers ?? []);
    setEditingSource(entry.source);
    setEditingSourceUrl(entry.sourceUrl);
    setEditingId(entry.id);
    setEditingEntry(entry);
    setCurrentFamilyId(entry.familyId ?? null);
    resetAiPanel();
    setShowForm(true);
  };

  const getActiveDocId = (): string | null => {
    if (editingId) return editingId;
    const trimmedName = form.name.trim();
    if (!trimmedName) return null;
    return buildCalibreDocId(
      trimmedName,
      form.brandId.trim() || form.brandName.trim() || undefined,
    );
  };

  // The tier form's manufacturer + image inputs. Tier images upload exactly as
  // the calibre's own do — into this calibre's image folder — so a tier image
  // is a storagePath like every other calibre image.
  const tierDocId = getActiveDocId();
  const tierMedia: TierMediaProps = {
    manufacturerOptions: manufacturers.map((m) => ({ id: m.id, name: m.displayName || m.name })),
    manufacturersLoading,
    uploadImage: tierDocId
      ? (file: Blob, fileName: string) => uploadCalibreImage(tierDocId, file, fileName)
      : undefined,
    resolveImageUrl: getImageUrl,
    fetchImage: fetchImageBlob,
    defaultImageSource: defaultCalibreSource,
    maxImages: MAX_IMAGES,
  };

  const uploadAndAppend = async (file: File | Blob, fileName: string) => {
    const docId = getActiveDocId();
    if (!docId) {
      toast.error('Enter calibre name (and brand) before adding images');
      return;
    }
    if (images.length >= MAX_IMAGES) {
      toast.error(`Max ${MAX_IMAGES} images`);
      return;
    }
    setUploadingImage(true);
    try {
      const storagePath = await uploadCalibreImage(docId, file, fileName);
      const caption = newImageCaption.trim();
      setImages((prev) => [
        ...prev,
        { storagePath, source: defaultCalibreSource, ...(caption ? { caption } : {}) },
      ]);
      setNewImageUrl('');
      setNewImageCaption('');
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleAddImageFromUrl = async () => {
    const url = newImageUrl.trim();
    if (!url) {
      toast.error('Image URL is required');
      return;
    }
    if (images.length >= MAX_IMAGES) {
      toast.error(`Max ${MAX_IMAGES} images`);
      return;
    }
    setUploadingImage(true);
    try {
      const blob = await fetchImageBlob(url);
      const fileName = url.split('/').pop()?.split('?')[0] || 'image.jpg';
      await uploadAndAppend(blob, fileName);
    } catch (error) {
      console.error('Failed to fetch image from URL:', error);
      toast.error('Failed to fetch image from URL');
      setUploadingImage(false);
    }
  };

  const handleAddImageFromFile = async (file: File) => {
    await uploadAndAppend(file, file.name);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpdateImageSource = (idx: number, source: string) => {
    setImages((prev) => prev.map((img, i) => (i === idx ? { ...img, source } : img)));
  };

  const handleRemoveImage = async (idx: number) => {
    const img = images[idx];
    setImages(images.filter((_, i) => i !== idx));
    if (img?.storagePath) {
      try {
        await deleteCalibreImage(img.storagePath);
      } catch (err) {
        // File may already be deleted or not exist — non-fatal
        console.warn('Failed to delete calibre image:', err);
      }
    }
  };

  // Dirty-tracking for the edit form so closing (Cancel / Escape / backdrop)
  // with unsaved changes warns instead of silently discarding. A successful
  // Save closes via handleSave (not this path), so it never prompts. Baseline is
  // snapshotted whenever the form opens (see effect below).
  const serializeFormState = () =>
    JSON.stringify({
      form,
      tiers,
      images,
      editingSource,
      editingSourceUrl,
    });
  const formBaselineRef = useRef<string>('');
  // The same baseline as STATE, for anything that must render from it (the
  // Reapply button). A ref alone is not enough: it is written after the render
  // that opened the form, so that render compared the new form against the
  // PREVIOUS calibre's snapshot and nothing re-rendered to correct it.
  const [formBaseline, setFormBaseline] = useState('');
  // On a rename, keep the old name as a universal alias so references that
  // still read it keep matching. On by default; a curator unticks it when the
  // old name was simply wrong (a typo) and should not live on.
  const [keepOldNameAsAlias, setKeepOldNameAsAlias] = useState(true);
  useEffect(() => {
    const snapshot = showForm ? serializeFormState() : '';
    formBaselineRef.current = snapshot;
    setFormBaseline(snapshot);
    // A fresh form starts from the default, whatever the last one was left on.
    setKeepOldNameAsAlias(true);
    // Snapshot when the form opens and whenever it is re-populated from another
    // entry while open; cleared on close so a reopen never inherits it. The live
    // form fields are read imperatively, so they're intentionally not deps here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, editingEntry]);

  // "Reapply to matching references" pushes the calibre AS STORED, so it is
  // unavailable while the form holds edits that have not been saved. The
  // baseline is empty until the snapshot effect has run, which reads as "clean".
  const [reapplyOpen, setReapplyOpen] = useState(false);
  const formHasUnsavedEdits =
    showForm && formBaseline !== '' && serializeFormState() !== formBaseline;
  const reapplyBlocked = editingEntry ? reapplyBlockedReason(editingEntry) : null;

  const handleCloseForm = () => {
    if (
      showForm &&
      serializeFormState() !== formBaselineRef.current &&
      !window.confirm('Discard unsaved calibre changes?')
    ) {
      return;
    }
    setShowForm(false);
    resetAiPanel();
    if (directEditMode) onClose();
  };

  const handleSave = async (skipConflictCheck = false, skipRenameCheck = false) => {
    if (!form.name.trim()) {
      toast.error('Calibre name is required');
      return;
    }
    // Symmetric ± spec: both bounds are unsigned spreads with lower <= upper.
    if (form.accuracySymmetric) {
      const lo = form.accuracyLowerSecPerDay !== '' ? parseFloat(form.accuracyLowerSecPerDay) : undefined;
      const hi = form.accuracyUpperSecPerDay !== '' ? parseFloat(form.accuracyUpperSecPerDay) : undefined;
      if ((lo !== undefined && lo < 0) || (hi !== undefined && hi < 0)) {
        toast.error('Symmetric accuracy spreads must be ≥ 0 (unsigned).');
        return;
      }
      if (lo !== undefined && hi !== undefined && lo > hi) {
        toast.error('Symmetric accuracy: best-case spread must be ≤ worst-case spread.');
        return;
      }
    }
    setSaving(true);
    try {
      // Tiers live inside CalibreData but are edited as separate state — merge
      // them back in. Sanitize each tier at the write boundary: the save path's
      // stripUndefined is shallow and won't recurse into array elements, so a
      // tier-level `description: undefined` (or any unset override key) would
      // make Firestore reject the write. Omit the key entirely when there are
      // none so an empty array isn't persisted.
      const cleanTiers = tiers.map(sanitizeTierForWrite);
      const data: CalibreData = {
        ...formToData(form),
        tiers: cleanTiers.length > 0 ? cleanTiers : undefined,
      };
      // A rename of an EXISTING calibre keeps its old name as a universal alias
      // (unless the curator opted out). Decided here, at the one write point,
      // so it holds however the name was changed — typed, or accepted from AI.
      const aliasDetailsToSave = withOldNameAlias(
        form.aliasDetails,
        editingId ? editingEntry?.name : undefined,
        form.name,
        keepOldNameAsAlias,
      );
      const aliasDetailsClean = aliasDetailsToSave
        .map((a) => ({ ...a, name: a.name.trim() }))
        .filter((a) => a.name.length > 0)
        // stripUndefined in the save path is shallow and doesn't recurse into
        // array elements — drop undefined keys per entry so Firestore doesn't
        // reject e.g. a cleared brandId as `undefined`.
        .map(
          (a) =>
            Object.fromEntries(
              Object.entries(a).filter(([, v]) => v !== undefined),
            ) as unknown as CalibreAliasDetail,
        );
      const aliasNames = aliasDetailsClean.map((a) => a.name);
      const aliasDetailsArg = aliasDetailsClean.length > 0 ? aliasDetailsClean : undefined;
      const aliasNamesArg = aliasNames.length > 0 ? aliasNames : undefined;
      const manufacturerIdArg = form.manufacturerId.trim() || undefined;
      // Always pass the array (even when empty) so clearing all co-developers
      // persists; the service cleans + excludes the primary and writes null when
      // empty. undefined would leave the stored value untouched.
      const additionalManufacturerIdsArg = form.additionalManufacturerIds;
      // Always passed: null clears it (the service also nulls one equal to the primary).
      const originalManufacturerIdArg = form.originalManufacturerId.trim() || null;
      const brandIdArg = form.brandId.trim() || undefined;
      // The editor owns the whole document, so a field the curator emptied has
      // to reach Firestore as an explicit null. Passing undefined (as these four
      // used to) drops the key from the update and `updateDoc` merges, leaving
      // the old value behind — clearing the Brand and saving put it straight
      // back on reload. `brandIdArg` stays undefined-shaped above because the
      // conflict check reads it as "no brand scope", not as a write.
      const brandIdWrite = form.brandId.trim() || null;
      const descriptionWrite = form.description.trim() || null;
      const sourceUrlWrite = editingSourceUrl?.trim() || null;
      const imagesWrite = images;

      // Warn (never merge) when a name/AKA genuinely collides with another
      // calibre. Brand-scoped aliases pointing at DIFFERENT brands are not
      // collisions and don't trigger this — see checkCalibreConflict. When a
      // real collision is found we abort the save and surface a warn-only modal
      // ("Save anyway" / "Go back"); nothing is written and no data is touched.
      if (!skipConflictCheck) {
        const conflict = await checkCalibreConflict(
          form.name.trim(),
          brandIdArg,
          aliasDetailsArg,
          editingId || undefined, // exclude current doc when editing
        );
        if (conflict) {
          setSaving(false);
          setConflictWarning({
            akaName: conflict.matchedName,
            otherCalibreName: conflict.name,
            otherCalibreId: conflict.id,
          });
          return;
        }
      }

      // Renaming a calibre that references are linked to: confirm first. Runs
      // after the conflict check, so "Save anyway" there still lands here. A
      // failed count never blocks the save — the warning is advisory.
      const renamedTo = form.name.trim();
      if (!skipRenameCheck && editingId && editingEntry && editingEntry.name.trim() !== renamedTo) {
        let refCount = 0;
        try {
          refCount = await getReferenceCountForCalibre(editingId);
        } catch (err) {
          console.warn('Could not count references for the rename warning:', err);
        }
        if (refCount > 0) {
          setSaving(false);
          setRenameWarning({ refCount, newName: renamedTo });
          return;
        }
      }

      // Persist. We NEVER merge into another calibre, and an edit NEVER moves a
      // calibre to a different doc. Editing always updates the exact doc being
      // edited (forceOverwriteId = self, replacing its fields) — including when
      // the name/brand changes and the id slug would no longer match. Doc ids
      // are storage keys, not display values; letting the slug drift is purely
      // cosmetic, whereas re-keying meant delete + re-create, which silently
      // dropped every field the create payload doesn't carry (familyId,
      // deprecated, redirectTo*, createdAt) and orphaned image storagePaths and
      // reference FKs. New entries still create their own doc.
      let savedId: string;
      if (editingId) {
        savedId = await saveCustomCalibre(
          form.name.trim(),
          data,
          form.brandName.trim() || undefined,
          aliasNamesArg,
          imagesWrite,
          sourceUrlWrite,
          true,
          descriptionWrite,
          editingId, // forceOverwriteId = self: update this doc only, no search
          false,
          manufacturerIdArg,
          aliasDetailsArg,
          brandIdWrite,
          editingSource?.trim() || defaultCalibreSource,
          additionalManufacturerIdsArg,
          originalManufacturerIdArg,
        );
        // Regression guard: an edit must land on the doc being edited. If a
        // future change ever reroutes the write to a new id, fail loudly here
        // rather than leaving a half-copied duplicate behind.
        if (savedId !== editingId) {
          throw new Error(
            `Calibre edit wrote to "${savedId}" but was editing "${editingId}" — aborted to avoid data loss.`,
          );
        }
      } else {
        savedId = await saveCustomCalibre(
          form.name.trim(),
          data,
          form.brandName.trim() || undefined,
          aliasNamesArg,
          imagesWrite,
          sourceUrlWrite,
          false,
          descriptionWrite,
          undefined,
          true, // forceCreate: write a fresh doc, no search/merge
          manufacturerIdArg,
          aliasDetailsArg,
          brandIdWrite,
          editingSource?.trim() || defaultCalibreSource,
          additionalManufacturerIdsArg,
          originalManufacturerIdArg,
        );
      }
      toast.success(editingId ? 'Custom calibre updated' : 'Custom calibre added');
      setShowForm(false);
      onSaved?.(savedId);
      if (directEditMode) {
        onClose();
      } else {
        await loadEntries(true);
      }
    } catch (error) {
      console.error('Failed to save custom calibre:', error);
      toast.error('Failed to save custom calibre');
    } finally {
      setSaving(false);
    }
  };

  // Reset base movement check when the field changes
  const handleBaseMovementChange = (value: string) => {
    updateForm({ baseMovement: value });
    setBaseMovementInDb(null);
  };

  const handleCheckBaseMovement = async () => {
    const name = form.baseMovement?.trim();
    if (!name) return;
    setBaseMovementChecking(true);
    try {
      const calibres = await getCustomCalibres();
      const nameLower = normalizeCalibreKey(name);
      const found = calibres.find((c: CustomCalibreEntry) => {
        if (normalizeCalibreKey(c.name) === nameLower) return true;
        return c.alsoKnownAs?.some((a: string) => normalizeCalibreKey(a) === nameLower);
      });
      setBaseMovementInDb(!!found);
      if (found) {
        toast.success(`"${found.name}" found in database`);
      } else {
        toast(`"${name}" not found in calibre database`);
      }
    } catch {
      setBaseMovementInDb(null);
    } finally {
      setBaseMovementChecking(false);
    }
  };

  const handleOpenBaseMovement = async () => {
    const name = form.baseMovement?.trim();
    if (!name) return;

    // Find the base calibre entry
    const calibres = await getCustomCalibres();
    const nameLower = normalizeCalibreKey(name);
    const found = calibres.find((c: CustomCalibreEntry) => {
      if (normalizeCalibreKey(c.name) === nameLower) return true;
      return c.alsoKnownAs?.some((a: string) => normalizeCalibreKey(a) === nameLower);
    });

    if (!found) {
      toast.error(`"${name}" not found in calibre database`);
      return;
    }

    // Check for unsaved changes
    const hasChanges = JSON.stringify(form) !== JSON.stringify(
      editingId ? entryToForm(entries.find(e => e.id === editingId) || { id: '', name: '', data: {} } as CustomCalibreEntry) : emptyForm()
    );

    if (hasChanges) {
      const action = window.confirm(
        'You have unsaved changes. Save them before navigating to the base calibre?\n\nOK = Save first\nCancel = Discard changes'
      );
      if (action) {
        // Save first, then navigate
        await handleSave();
      }
    }

    // Store current entry so we can return to it
    if (editingId) {
      const currentEntry = entries.find(e => e.id === editingId);
      if (currentEntry) setReturnToEntry(currentEntry);
    }

    // Load the base movement entry
    setEditingId(found.id);
    setForm(entryToForm(found));
    setImages(found.images || []);
    setEditingSource(found.source);
    setEditingSourceUrl(found.sourceUrl);
    setCurrentFamilyId(found.familyId ?? null);
    setBaseMovementInDb(null);
    resetAiPanel();
    toast.success(`Editing "${found.name}"`);
  };

  const handleReturnFromBaseMovement = async () => {
    if (!returnToEntry) return;
    // Reload the entry from DB to get fresh data
    const calibres = await getCustomCalibres(true);
    const fresh = calibres.find(c => c.id === returnToEntry.id);
    const entry = fresh || returnToEntry;
    setEditingId(entry.id);
    setForm(entryToForm(entry));
    setImages(entry.images || []);
    setEditingSource(entry.source);
    setEditingSourceUrl(entry.sourceUrl);
    setCurrentFamilyId(entry.familyId ?? null);
    setReturnToEntry(null);
    setBaseMovementInDb(null);
    resetAiPanel();
  };

  const handleDelete = async (entry: CustomCalibreEntry) => {
    setDeletingId(entry.id);
    try {
      await deleteCustomCalibre(entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      toast.success(`Deleted "${entry.name}"`);
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete custom calibre');
    } finally {
      setDeletingId(null);
    }
  };

  const updateForm = (updates: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  // Lookup helpers used by the brand picker. brandOptions is the cached list
  // of watchBrands; brandById gives O(1) chip rendering for the matched case.
  const brandById = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of brandOptions) m.set(b.id, b.name);
    return m;
  }, [brandOptions]);

  // Curator documentation for the brand and the manufacturer currently on the
  // form. The brand's links come free with the brand list; the manufacturer's
  // are fetched on demand, because the manufacturer dropdown is served from the
  // Algolia catalogue and that projection deliberately omits `docs`.
  const brandDocsById = useMemo(() => {
    const m = new Map<string, DocsLink[]>();
    for (const b of brandOptions) if (b.docs?.length) m.set(b.id, b.docs);
    return m;
  }, [brandOptions]);
  const { docs: manufacturerDocs } = useDocsLinks(
    'manufacturer',
    form.manufacturerId,
  );

  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');
  const filteredBrandOptions = useMemo(() => {
    const term = brandSearch.trim().toLowerCase();
    const list = term
      ? brandOptions.filter((b) => b.name.toLowerCase().includes(term) || b.id.toLowerCase().includes(term))
      : brandOptions;
    return list.slice(0, 50);
  }, [brandOptions, brandSearch]);

  const handlePickBrand = (id: string, name: string) => {
    setForm((prev) => ({ ...prev, brandId: id, brandName: name }));
    setBrandPickerOpen(false);
    setBrandSearch('');
  };

  const handleClearBrand = () => {
    setForm((prev) => ({ ...prev, brandId: '', brandName: '' }));
  };

  // Manufacturer quick-create modal state.
  const [showCreateMfr, setShowCreateMfr] = useState(false);

  const handleManufacturerChange = (value: string) => {
    if (value === '__create__') {
      setShowCreateMfr(true);
      return;
    }
    // A manufacturer is never both primary and co-developer — drop it from the
    // additional list if it was just promoted to primary.
    setForm((prev) => ({
      ...prev,
      manufacturerId: value,
      additionalManufacturerIds: prev.additionalManufacturerIds.filter((id) => id !== value),
      // The original maker only means something when it differs from the primary.
      originalManufacturerId: prev.originalManufacturerId === value ? '' : prev.originalManufacturerId,
    }));
  };

  // No local splice: `createMovementManufacturer` records the new manufacturer
  // in the catalogue overlay and broadcasts the change, so this dropdown — and
  // every other one in the app — already has it by the time this runs, whether
  // or not Algolia has indexed it yet. See `manufacturerCatalogueOverlay.ts`.
  // All that is left here is selecting it.
  const handleMfrCreated = (id: string) => {
    setForm((prev) => ({
      ...prev,
      manufacturerId: id,
      additionalManufacturerIds: prev.additionalManufacturerIds.filter((x) => x !== id),
      originalManufacturerId: prev.originalManufacturerId === id ? '' : prev.originalManufacturerId,
    }));
    setShowCreateMfr(false);
  };

  // Co-developer (additional manufacturer) add/remove. The picker only offers
  // manufacturers that are neither the primary nor already selected, so adds are
  // always unique; the guard is defensive.
  const handleAddCoDeveloper = (value: string) => {
    if (!value) return;
    setForm((prev) =>
      value === prev.manufacturerId || prev.additionalManufacturerIds.includes(value)
        ? prev
        : { ...prev, additionalManufacturerIds: [...prev.additionalManufacturerIds, value] },
    );
  };

  const handleRemoveCoDeveloper = (value: string) => {
    setForm((prev) => ({
      ...prev,
      additionalManufacturerIds: prev.additionalManufacturerIds.filter((id) => id !== value),
    }));
  };

  // AI enrichment flow. The reference-info textarea + editable correction form
  // are the shared AiCalibreEnrichPanel; this modal's accept handler merges the
  // reviewed draft into the edit form (filling empty fields only — existing
  // curated values are never overwritten) so the user can review before saving.

  /** Seed `existingData` from the current form so the AI treats already-known
   *  values as ground truth and doesn't contradict them. */
  const buildExistingFromForm = (): Record<string, unknown> => {
    const e: Record<string, unknown> = {};
    if (form.movementType) e.movementType = form.movementType;
    if (form.jewels) e.jewels = form.jewels;
    if (form.powerReserveHours) e.powerReserveHours = form.powerReserveHours;
    if (form.frequencyVph) e.frequencyVph = form.frequencyVph;
    if (form.diameterMm) e.diameterMm = form.diameterMm;
    if (form.heightMm) e.heightMm = form.heightMm;
    if (form.baseMovement) e.baseMovement = form.baseMovement;
    if (form.windingDirection) e.windingDirection = form.windingDirection;
    if (form.antiShockSystem) e.antiShockSystem = form.antiShockSystem;
    if (form.countryOfManufacture) e.countryOfManufacture = form.countryOfManufacture;
    if (form.escapement) e.escapement = form.escapement;
    return e;
  };

  /** Clear the AI panel state — used when switching entries or closing the form. */
  const resetAiPanel = () => {
    setAiState({});
    setShowAiPanel(false);
    setRefInfo('');
    setAiApplyOpen(false);
    setAiFieldDiffs([]);
    setAiFieldSel({});
    setAiTierSel({});
    setAiTierRemap({});
    setAiTierRename({});
    setAiTierMode({});
    setAiTierMergeFields({});
  };

  /**
   * AI-proposed tier variants from the current draft, each flagged with whether
   * a tier of the same name already exists on the calibre (case-insensitive).
   * Existing-name tiers default OFF so the merge is append-only and never
   * clobbers a user-created tier.
   */
  const aiProposedTiers = useMemo<
    Array<{
      tier: AIProposedCalibreTier;
      exists: boolean;
      overrideCount: number;
      /** The existing tier this proposal's name matches, if any. */
      matchedTier?: CalibreTier;
      /** The fields a merge into `matchedTier` could overlay. */
      mergeRows: MergeableTierField[];
      /** An existing tier that is another spelling of this proposal's listed grade. */
      vocabMatch?: { tier: CalibreTier; to: string };
    }>
  >(() => {
    const g = tierNameOptions(tierVocabulary);
    const genericOptions = [...g.manufacturer, ...g.family];
    const proposed = aiState.draft?.tiers ?? [];
    // Matched by NORMALIZED name, the same test `exists` has always used, so a
    // proposal named "c" reaches the tier called "C". First match wins; two
    // tiers sharing a normalized name would be a data fault either way.
    const byName = new Map<string, CalibreTier>();
    for (const t of tiers) {
      const key = t.name.trim().toLowerCase();
      if (!byName.has(key)) byName.set(key, t);
    }
    return proposed.map((tier) => {
      const matchedTier = byName.get(tier.name.trim().toLowerCase());
      return {
        tier,
        exists: !!matchedTier,
        overrideCount: Object.values(tier.overrides ?? {}).filter(
          (v) => v !== undefined && v !== null && v !== '',
        ).length,
        matchedTier,
        mergeRows: matchedTier ? mergeableTierFields(matchedTier, tier) : [],
        vocabMatch: matchedTier ? undefined : (vocabRenameTarget(tier.name, tiers, genericOptions) ?? undefined),
      };
    });
  }, [aiState.draft, tiers, tierVocabulary]);

  /**
   * The merge choice per proposal index, for `planProposedTierMerge`. Only
   * name-matched rows in 'merge' mode produce one; the grouped `accuracy` row
   * is expanded here to the override keys it actually writes, since the plan
   * honours real keys only.
   */
  const aiTierMergeChoices = useMemo<Record<number, ProposedTierMergeChoice>>(() => {
    const out: Record<number, ProposedTierMergeChoice> = {};
    aiProposedTiers.forEach(({ matchedTier, mergeRows }, i) => {
      if (!matchedTier || (aiTierMode[i] ?? 'merge') !== 'merge') return;
      const ticked = aiTierMergeFields[i];
      if (!ticked) return;
      const fields = mergeRows
        .filter((row) => ticked.has(row.key))
        .flatMap((row) => (row.key === 'description' ? ['description'] : row.overrideKeys));
      if (fields.length === 0) return;
      out[i] = { targetId: matchedTier.id, fields };
    });
    return out;
  }, [aiProposedTiers, aiTierMode, aiTierMergeFields]);

  /**
   * Apply the ticked AI-proposed tiers to `tiers` and report how many landed.
   *
   * The rules — remapped proposals merge fill-empty into the chosen tier, the
   * rest append under a fresh id, name clashes are skipped — live in
   * `planProposedTierMerge`, which is pure and tested. This is the state wiring
   * around it, run synchronously against the current `tiers` so the count is
   * right immediately (setTiers' updater runs later).
   */
  const mergeProposedTiers = (keys: Set<number>): number => {
    const plan = planProposedTierMerge({
      tiers,
      proposed: aiProposedTiers.map(({ tier }) => tier),
      accepted: keys,
      remap: aiTierRemap,
      merge: aiTierMergeChoices,
      rename: aiTierRename,
    });
    if (plan.created > 0 || plan.merged > 0) setTiers(plan.tiers);
    return plan.created + plan.merged;
  };

  const runAiEnrich = async (opts?: { existingData?: Record<string, unknown> }) => {
    const name = form.name.trim();
    if (!name) {
      toast.error('Enter a calibre name first');
      return;
    }
    setAiState({ loading: true });
    try {
      const seeded = buildExistingFromForm();
      const existingData = opts?.existingData ?? (Object.keys(seeded).length > 0 ? seeded : undefined);
      const result = await enrichCalibreWithAI(name, {
        brandName: form.brandName.trim() || currentBrandName || undefined,
        description: form.description?.trim() || undefined,
        referenceInfo: refInfo.trim() || undefined,
        existingData,
        // Tier vocabulary the curator already holds, so the model reuses an
        // established name rather than coining a synonym. General tiers only:
        // calibre enrichment never considers brand tiers (James, 2026-10-29) —
        // a shared base movement carries every brand's modifications.
        existingTierNames: tiersForBrand(tiers, undefined).map((t) => t.name),
        familyTierVocab: familyTierNames,
        // Structured vocabulary: the server loads the manufacturer's and the
        // family's tier names, and gates brand tiers on this brand.
        manufacturerId: tierManufacturerId || undefined,
        familyId: currentFamilyId || undefined,
        brandId: form.brandId.trim() || undefined,
        // A revision-line member gets a REVISION LINE block, so its sibling
        // revisions are not proposed as tiers (lineage Phase 5).
        calibreId: editingId || undefined,
      });
      setAiState({ result, draft: resultToDraft(result) });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'AI lookup failed';
      setAiState({ error: msg });
    }
  };

  // Re-run, feeding the user's current (edited) field values back as ground truth.
  const handleRerunWithCorrections = () => {
    const draft = aiState.draft;
    const existingData: Record<string, unknown> = { ...(draft?.specs || {}) };
    if (draft?.canonicalName?.trim()) existingData.canonicalName = draft.canonicalName.trim();
    void runAiEnrich({ existingData });
  };

  /**
   * Build the current-vs-proposed diff for the (possibly user-edited) draft and
   * open the per-field apply review. This replaces the old fill-empty-only
   * accept: the review lets the user OVERRIDE existing curated values per field
   * (e.g. flip movementType quartz→automatic, or replace the description) — the
   * behaviour the fill-empty rule silently blocked. Nothing is written to the
   * form until the user picks fields and confirms; they still click Save/Update.
   */
  const openAiApplyReview = () => {
    const draft = aiState.draft;
    if (!draft) return;
    const data = aiSpecsToCalibreData(draft.specs);
    const diffs: AiFieldDiff[] = [];

    // Canonical name.
    const newName = draft.canonicalName.trim();
    if (newName) {
      diffs.push({
        key: NAME_KEY,
        label: humanizeAiKey(NAME_KEY),
        current: aiDisplayValue(form.name),
        proposed: aiDisplayValue(newName),
        changed: newName.toLowerCase() !== form.name.trim().toLowerCase(),
      });
    }

    // Description.
    const newDesc = draft.description.trim();
    if (newDesc) {
      diffs.push({
        key: DESCRIPTION_KEY,
        label: humanizeAiKey(DESCRIPTION_KEY),
        current: aiDisplayValue(form.description),
        proposed: aiDisplayValue(newDesc),
        changed: newDesc !== form.description.trim(),
      });
    }

    // Spec fields. Form field names align with CalibreData keys (numbers stored
    // as strings); compare the apply-shaped value so "changed" is accurate.
    const formRec = form as unknown as Record<string, unknown>;
    for (const [k, v] of Object.entries(data)) {
      if (v == null) continue;
      const applyVal = typeof v === 'number' ? String(v) : v;
      const cur = formRec[k];
      const changed = JSON.stringify(cur ?? '') !== JSON.stringify(applyVal ?? '');
      diffs.push({
        key: k,
        label: humanizeAiKey(k),
        current: aiDisplayValue(cur),
        proposed: aiDisplayValue(v),
        changed,
      });
    }

    if (diffs.length === 0 && aiProposedTiers.length === 0) {
      toast('AI returned no fields to apply', { icon: 'ℹ️' });
      return;
    }

    // Default-tick only the fields that would actually change something.
    const sel: Record<string, boolean> = {};
    for (const d of diffs) sel[d.key] = d.changed;
    // Default-tick proposed tiers whose name isn't already on the calibre
    // (append-only). Existing-name proposals start OFF and are skipped on merge.
    const tierSel: Record<number, boolean> = {};
    // Every offered field starts ticked, so accepting a merge takes one click
    // and narrowing it is the deliberate act. Seeded for every name-matched row
    // whether or not it is accepted — the picker is ready the moment it opens.
    const mergeFields: Record<number, Set<string>> = {};
    // A proposal that is the listed spelling of a grade an existing tier holds
    // under another spelling starts mapped onto that tier, with the rename
    // ticked — the merge the enrichCalibre prompt promises the model.
    const remapSeed: Record<number, string> = {};
    const renameSeed: Record<number, string> = {};
    aiProposedTiers.forEach(({ exists, mergeRows, vocabMatch }, i) => {
      tierSel[i] = !exists;
      if (exists) mergeFields[i] = new Set(mergeRows.map((row) => row.key));
      if (vocabMatch) {
        remapSeed[i] = vocabMatch.tier.id;
        renameSeed[i] = vocabMatch.to;
      }
    });
    setAiFieldDiffs(diffs);
    setAiFieldSel(sel);
    setAiTierSel(tierSel);
    setAiTierRemap(remapSeed);
    setAiTierRename(renameSeed);
    setAiTierMode({});
    setAiTierMergeFields(mergeFields);
    setAiApplyOpen(true);
  };

  /** Write the selected AI fields onto the form, overwriting existing values.
   *  Renames preserve the original name as an alias. */
  const applyAiFields = (keys: Set<string>, tierKeys: Set<number>) => {
    const draft = aiState.draft;
    if (!draft) return;
    const data = aiSpecsToCalibreData(draft.specs);
    // Merge accepted tier proposals into tiers[] (append-only; skips name
    // clashes). Done before the state reset below reads a stale draft.
    const mergedTiers = mergeProposedTiers(tierKeys);
    setForm((prev) => {
      const next = { ...prev };
      const newName = draft.canonicalName.trim();
      // Default the owning manufacturer to the brand only when the calibre is
      // in-house (same heuristic as the toolbar/bulk AI flows). Fill only when
      // the user hasn't already chosen a manufacturer, so we never clobber a
      // deliberate pick. brandId is the form's contextual brand.
      if (!prev.manufacturerId.trim() && prev.brandId.trim()) {
        const manufacturer =
          typeof draft.specs.manufacturer === 'string' ? draft.specs.manufacturer.trim() : undefined;
        const inHouseManufacturerId = resolveBrandManufacturerId({
          specs: draft.specs,
          brandName: prev.brandName,
          brandId: prev.brandId.trim(),
          isGeneric: isGenericMovementName(newName || prev.name, manufacturer),
        });
        if (inHouseManufacturerId) next.manufacturerId = inHouseManufacturerId;
      }
      if (keys.has(NAME_KEY) && newName && newName.toLowerCase() !== prev.name.toLowerCase()) {
        // An EXISTING calibre's old name is preserved at save time instead
        // (see handleSave → withOldNameAlias), under the curator's "Keep old
        // name as alias" tick. This demotion now covers only a calibre still
        // being created, where there is no stored name for the save to compare.
        const existingAliases = prev.aliasDetails.map((a) => a.name.toLowerCase());
        if (!editingId && !existingAliases.includes(prev.name.toLowerCase())) {
          // Brand-scope the demoted old name when this calibre belongs to a brand,
          // so a short/ambiguous alias (e.g. "630") only matches refs of that brand.
          // Stays universal for movement-manufacturer calibres with no brand.
          const demoted: CalibreAliasDetail = prev.brandId.trim()
            ? { name: prev.name, brandId: prev.brandId.trim(), showInExplore: false }
            : { name: prev.name, showInExplore: false };
          next.aliasDetails = [...prev.aliasDetails, demoted];
        }
        next.name = newName;
      }
      if (keys.has(DESCRIPTION_KEY) && draft.description.trim()) {
        next.description = draft.description.trim();
      }
      // Overlay selected CalibreData fields. Field names align (form stores
      // numbers as strings). Unlike the old accept, this OVERWRITES existing
      // values — the whole point of the per-field opt-in.
      for (const [k, v] of Object.entries(data)) {
        if (!keys.has(k) || v == null) continue;
        (next as Record<string, unknown>)[k] = typeof v === 'number' ? String(v) : v;
      }
      return next;
    });
    setAiApplyOpen(false);
    setShowAiPanel(false);
    setAiState({});
    setAiFieldDiffs([]);
    setAiFieldSel({});
    setAiTierSel({});
    setAiTierRemap({});
    setAiTierRename({});
    setAiTierMode({});
    setAiTierMergeFields({});
    const n = keys.size;
    const parts: string[] = [];
    if (n > 0) parts.push(`${n} AI field${n === 1 ? '' : 's'}`);
    if (mergedTiers > 0) parts.push(`${mergedTiers} tier${mergedTiers === 1 ? '' : 's'}`);
    toast.success(
      parts.length > 0 ? `Applied ${parts.join(' + ')} to form` : 'Nothing applied',
    );
  };

  const aiSelectedCount = useMemo(
    () => aiFieldDiffs.filter((d) => aiFieldSel[d.key]).length,
    [aiFieldDiffs, aiFieldSel],
  );

  /**
   * Ticked proposals that would actually do something. A name-matched row in
   * 'merge' mode with every field unticked is ticked-but-inert, and counting it
   * would have the Apply button promise a write it won't make.
   */
  const aiSelectedTierCount = useMemo(
    () =>
      aiProposedTiers.filter(({ matchedTier }, i) => {
        if (!aiTierSel[i]) return false;
        if (!matchedTier || (aiTierMode[i] ?? 'merge') !== 'merge') return true;
        return !!aiTierMergeChoices[i];
      }).length,
    [aiProposedTiers, aiTierSel, aiTierMode, aiTierMergeChoices],
  );

  const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm';
  const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

  if (showForm) {
    return (
      <Modal isOpen={isOpen} onClose={handleCloseForm} title={editingId ? 'Edit Custom Calibre' : 'Add Custom Calibre'} size='full'
        headerActions={
          <CopyLinkButton
            url={
              editingId
                ? buildShareUrl('calibre', editingId, editingEntry?.name)
                : null
            }
            noun='calibre'
          />
        }
      >
        <div className='space-y-4'>
          {requestNote && (
            <div className='rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 whitespace-pre-line'>
              {requestNote}
            </div>
          )}
          {editingEntry?.deprecated && (
            <div className='rounded-lg bg-gray-100 border border-gray-300 px-3 py-2.5 text-sm text-gray-700 space-y-2'>
              <div className='flex items-center gap-2'>
                <span className='inline-flex items-center rounded-full bg-gray-500 px-2 py-0.5 text-[11px] font-semibold text-white'>
                  Deprecated
                </span>
                <span>
                  This calibre was reclassified as a brand-specific tier of
                  another calibre, and is now a soft-deleted redirect alias.
                  Editing is permitted for cleanup — fixing an incomplete
                  migration, tidying AKAs, correcting the name — but changes only
                  affect the alias itself.
                </span>
              </div>
              <div className='text-xs text-gray-600'>
                Redirects to{' '}
                <span className='font-mono'>
                  {editingEntry.redirectToCalibreId}
                </span>
                {editingEntry.redirectToTierId && (
                  <>
                    {' '}
                    · tier{' '}
                    <span className='font-mono'>
                      {editingEntry.redirectToTierId}
                    </span>
                  </>
                )}
                . The resolver skips deprecated calibres, so nothing resolves
                here any more — references were re-pointed at the target and the
                alias is kept for historical reference.
              </div>
            </div>
          )}
          {returnToEntry && (
            <button
              type='button'
              onClick={handleReturnFromBaseMovement}
              className='flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800'
            >
              ← Back to {returnToEntry.name}
            </button>
          )}
          {/* Identity */}
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <div className='flex items-center justify-between mb-1'>
                <label className='text-xs font-medium text-gray-600'>Calibre Name *</label>
                <button
                  type='button'
                  onClick={() => setShowAiPanel((v) => !v)}
                  disabled={!form.name.trim()}
                  className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer'
                  title='Identify and fill in specs using AI'
                >
                  {showAiPanel ? '✕ Hide AI' : '✨ Suggest with AI'}
                </button>
              </div>
              <input type='text' value={form.name} onChange={(e) => updateForm({ name: e.target.value })} className={inputClass} placeholder='e.g., Angelus A-300' />
              {editingId && editingEntry && calibreWasRenamed(editingEntry.name, form.name) && (
                <label
                  className='mt-1.5 flex items-start gap-1.5 text-xs text-gray-600'
                  title='References that still store the old name keep matching this calibre. Untick when the old name was a typo.'
                >
                  <input
                    type='checkbox'
                    checked={keepOldNameAsAlias}
                    onChange={(e) => setKeepOldNameAsAlias(e.target.checked)}
                    className='mt-0.5 rounded text-brand-600 focus:ring-brand-500'
                  />
                  <span>
                    Keep old name as alias{' '}
                    <span className='text-gray-500'>(“{editingEntry.name.trim()}”, all brands)</span>
                  </span>
                </label>
              )}
            </div>
            <div>
              <div className='flex items-center justify-between mb-1'>
                <label className='text-xs font-medium text-gray-600'>Brand (optional — leave empty for global)</label>
                {currentBrandName && !form.brandId && (
                  <button
                    type='button'
                    onClick={() => {
                      // Try to resolve currentBrandName to a brand doc id; fall
                      // back to legacy text-only if no match.
                      const lower = currentBrandName.toLowerCase();
                      const match = brandOptions.find(
                        (b) => b.name.toLowerCase() === lower || b.id.toLowerCase() === lower,
                      );
                      if (match) handlePickBrand(match.id, match.name);
                      else setForm((prev) => ({ ...prev, brandName: currentBrandName }));
                    }}
                    className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors cursor-pointer'
                  >
                    Use {currentBrandName}
                  </button>
                )}
              </div>
              {form.brandId ? (
                <div className='flex items-center gap-2'>
                  <span className='inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium bg-brand-50 text-brand-800 border border-brand-200'>
                    {brandById.get(form.brandId) || form.brandName || form.brandId}
                    <button
                      type='button'
                      onClick={handleClearBrand}
                      className='ml-1 text-brand-500 hover:text-brand-700'
                      title='Clear brand'
                    >
                      ×
                    </button>
                  </span>
                  <button
                    type='button'
                    onClick={() => setBrandPickerOpen(true)}
                    className='text-xs text-brand-600 hover:text-brand-800 underline'
                  >
                    Change
                  </button>
                  <DocsLinkChip
                    links={brandDocsById.get(form.brandId)}
                    entityLabel={brandById.get(form.brandId) || form.brandId}
                  />
                </div>
              ) : form.brandName ? (
                <div className='flex items-center gap-2 flex-wrap'>
                  <span
                    className='inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200'
                    title='Legacy free-text brand value — not yet mapped to a watchBrand doc'
                  >
                    legacy: {form.brandName}
                  </span>
                  <button
                    type='button'
                    onClick={() => setBrandPickerOpen(true)}
                    className='text-xs text-amber-700 hover:text-amber-900 underline'
                  >
                    Map…
                  </button>
                  <button
                    type='button'
                    onClick={handleClearBrand}
                    className='text-xs text-gray-500 hover:text-gray-700 underline'
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <button
                  type='button'
                  onClick={() => setBrandPickerOpen(true)}
                  className={`${inputClass} text-left text-gray-500 hover:bg-gray-50`}
                >
                  Pick a brand…
                </button>
              )}
              {brandPickerOpen && (
                <div className='mt-2 border border-gray-200 rounded-md bg-white shadow-sm'>
                  <input
                    type='text'
                    value={brandSearch}
                    onChange={(e) => setBrandSearch(e.target.value)}
                    placeholder='Search brands…'
                    autoFocus
                    className='w-full px-2 py-1.5 border-b border-gray-200 text-sm focus:outline-none'
                  />
                  <div className='max-h-48 overflow-y-auto divide-y divide-gray-100'>
                    {filteredBrandOptions.length === 0 ? (
                      <div className='px-2 py-2 text-xs text-gray-500 italic'>
                        {brandOptions.length === 0 ? 'Loading…' : 'No brands match.'}
                      </div>
                    ) : (
                      filteredBrandOptions.map((b) => (
                        <button
                          key={b.id}
                          type='button'
                          onClick={() => handlePickBrand(b.id, b.name)}
                          className='w-full text-left px-2 py-1.5 text-sm hover:bg-brand-50'
                        >
                          {b.name}{' '}
                          <span className='text-xs text-gray-400 font-mono'>{b.id}</span>
                        </button>
                      ))
                    )}
                  </div>
                  <div className='flex justify-end gap-2 px-2 py-1.5 border-t border-gray-100 bg-gray-50 text-xs'>
                    <button
                      type='button'
                      onClick={() => {
                        setBrandPickerOpen(false);
                        setBrandSearch('');
                      }}
                      className='text-gray-600 hover:text-gray-800 underline'
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className='col-span-2'>
              <label className={labelClass}>Primary manufacturer</label>
              <select
                value={form.manufacturerId}
                onChange={(e) => handleManufacturerChange(e.target.value)}
                disabled={manufacturersLoading}
                className={inputClass}
              >
                <option value=''>
                  {manufacturersLoading ? 'Loading manufacturers…' : '— None —'}
                </option>
                <option value='__create__'>+ Create new manufacturer…</option>
                {manufacturers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName || m.name}
                  </option>
                ))}
              </select>
              {manufacturerDocs.length > 0 && (
                <DocsLinkChip
                  links={manufacturerDocs}
                  className='mt-1.5'
                  entityLabel={
                    manufacturerById.get(form.manufacturerId)?.displayName ||
                    manufacturerById.get(form.manufacturerId)?.name ||
                    form.manufacturerId
                  }
                />
              )}
            </div>
            <div className='col-span-2'>
              <label className={labelClass}>Co-developed with (optional)</label>
              {form.additionalManufacturerIds.length > 0 && (
                <div className='flex flex-wrap gap-1.5 mb-2'>
                  {form.additionalManufacturerIds.map((id) => {
                    const m = manufacturers.find((x) => x.id === id);
                    return (
                      <span
                        key={id}
                        className='inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-50 text-brand-800 border border-brand-200'
                      >
                        {m ? m.displayName || m.name : id}
                        <button
                          type='button'
                          onClick={() => handleRemoveCoDeveloper(id)}
                          className='text-brand-500 hover:text-brand-700'
                          title='Remove co-developer'
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <select
                value=''
                onChange={(e) => {
                  handleAddCoDeveloper(e.target.value);
                  e.target.value = '';
                }}
                disabled={manufacturersLoading || !form.manufacturerId}
                className={inputClass}
              >
                <option value=''>
                  {!form.manufacturerId
                    ? 'Select a primary manufacturer first…'
                    : '+ Add a co-developer…'}
                </option>
                {manufacturers
                  .filter(
                    (m) =>
                      m.id !== form.manufacturerId &&
                      !form.additionalManufacturerIds.includes(m.id),
                  )
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName || m.name}
                    </option>
                  ))}
              </select>
              <p className='mt-1 text-xs text-gray-400'>
                For calibres jointly developed by multiple makers (e.g. UWD 33.1 = UWD + Sinn).
                The calibre appears under each manufacturer in the app.
              </p>
            </div>
            <div className='col-span-2'>
              <label className={labelClass} htmlFor='calibre-original-manufacturer'>
                Originally manufactured by (optional)
              </label>
              <select
                id='calibre-original-manufacturer'
                value={form.originalManufacturerId}
                onChange={(e) => setForm((prev) => ({ ...prev, originalManufacturerId: e.target.value }))}
                disabled={manufacturersLoading || !form.manufacturerId}
                className={inputClass}
              >
                <option value=''>
                  {!form.manufacturerId ? 'Select a primary manufacturer first…' : 'Same as the manufacturer'}
                </option>
                {form.originalManufacturerId &&
                  !manufacturers.some((m) => m.id === form.originalManufacturerId) && (
                    <option value={form.originalManufacturerId}>{form.originalManufacturerId}</option>
                  )}
                {manufacturers
                  .filter((m) => m.id !== form.manufacturerId)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName || m.name}
                    </option>
                  ))}
              </select>
              <p className='mt-1 text-xs text-gray-400'>
                The maker this calibre came from, when it is now made by someone else (e.g. ETA 6498-1 was
                originally Unitas). Not a co-developer. Lets it join that maker&rsquo;s revision line.
              </p>
            </div>
            {editingId && form.manufacturerId.trim() && (
              <CalibreRevisionsCard
                calibreId={editingId}
                manufacturerId={form.manufacturerId.trim()}
                originalManufacturerId={form.originalManufacturerId.trim() || undefined}
                onCalibreChanged={() => {
                  // A split or an AKA move rewrote this calibre's aliases. Take
                  // the stored list into the form, or the next Save would write
                  // the stale one back. Other unsaved edits are left alone.
                  void getCustomCalibreById(editingId).then((fresh) => {
                    if (fresh) setForm((prev) => ({ ...prev, aliasDetails: entryToForm(fresh).aliasDetails }));
                  });
                }}
              />
            )}
            {editingId ? (
              <CalibreFamilyField
                calibreId={editingId}
                manufacturerId={form.manufacturerId}
                familyId={currentFamilyId}
                onFamilyChange={setCurrentFamilyId}
              />
            ) : (
              <div className='col-span-2'>
                <label className={labelClass}>Movement Family</label>
                <p className='text-xs text-gray-400 italic'>
                  Save this calibre first to assign it to a family.
                </p>
              </div>
            )}
            <div className='col-span-2'>
              <label className={labelClass}>Also Known As</label>
              <div className='border border-gray-200 rounded-lg bg-white'>
                {form.aliasDetails.length > 0 && (
                  <div className='divide-y divide-gray-100'>
                    {form.aliasDetails.map((alias, idx) => (
                      <div key={idx} className='flex items-center gap-2 px-2 py-1.5'>
                        <input
                          type='text'
                          value={alias.name}
                          onChange={(e) => handleUpdateAlias(idx, { name: e.target.value })}
                          className='flex-1 border border-gray-200 rounded px-2 py-1 text-sm'
                          placeholder='Alias name'
                        />
                        <select
                          value={alias.brandId || ''}
                          onChange={(e) => handleUpdateAlias(idx, { brandId: e.target.value || undefined })}
                          className='border border-gray-200 rounded px-2 py-1 text-xs max-w-[12rem]'
                          title={
                            aliasBrandScope
                              ? "Brand for this alias — narrowed to this calibre's manufacturer"
                              : 'Optional brand for this alias'
                          }
                        >
                          <option value=''>— Any brand —</option>
                          {aliasBrandOptions.map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                          ))}
                          {/* An already-scoped alias whose brand falls outside the
                              narrowed list still renders its own value — otherwise
                              the row would read as "Any brand" while holding a
                              brandId, and the next edit to any OTHER field would
                              ship that silent mismatch. */}
                          {alias.brandId &&
                            !aliasBrandOptions.some((b) => b.id === alias.brandId) && (
                              <option value={alias.brandId}>
                                {brandById.get(alias.brandId) || alias.brandId} (outside
                                manufacturer)
                              </option>
                            )}
                        </select>
                        <label className='flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap'>
                          <input
                            type='checkbox'
                            checked={!!alias.showInExplore}
                            onChange={(e) => handleUpdateAlias(idx, { showInExplore: e.target.checked })}
                            className='rounded'
                          />
                          Show in Explore
                        </label>
                        <button
                          type='button'
                          onClick={() => handleRemoveAlias(idx)}
                          className='text-red-400 hover:text-red-600 text-sm px-1'
                          title='Remove alias'
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className='flex items-center gap-2 px-2 py-1.5 border-t border-gray-100 bg-gray-50 rounded-b-lg'>
                  <input
                    type='text'
                    value={newAliasName}
                    onChange={(e) => setNewAliasName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddAlias();
                      }
                    }}
                    placeholder='Add alias (e.g., Elaboré grade, 26-jewel Swiss automatic movement)...'
                    className='flex-1 border border-gray-200 rounded px-2 py-1 text-sm'
                  />
                  <button
                    type='button'
                    onClick={handleAddAlias}
                    disabled={!newAliasName.trim()}
                    className='px-3 py-1 text-xs font-medium rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed'
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
            <div className='col-span-2'>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Description</label>
              <textarea
                value={form.description}
                onChange={(e) => updateForm({ description: e.target.value })}
                className={inputClass}
                rows={4}
                placeholder='History, notable references, technical significance...'
              />
            </div>
            {showAiPanel && (
              <div className='col-span-2 border border-purple-200 bg-purple-50 rounded-lg p-3'>
                <div className='text-[10px] text-amber-700 bg-amber-50 px-2 py-1 rounded mb-3'>
                  ⚠️ AI suggestions may contain errors — verify before accepting. Accepting
                  fills empty fields only; existing values are never overwritten.
                </div>
                <AiCalibreEnrichPanel
                  state={aiState}
                  referenceInfo={refInfo}
                  onReferenceInfoChange={setRefInfo}
                  onRun={() => runAiEnrich()}
                  onDraftChange={(d) => setAiState((s) => ({ ...s, draft: d }))}
                  onAccept={openAiApplyReview}
                  onRerun={() => runAiEnrich()}
                  onRerunWithCorrections={handleRerunWithCorrections}
                  onClose={() => { setShowAiPanel(false); setAiState({}); }}
                  acceptLabel='Review & apply…'
                />
              </div>
            )}
          </div>

          {/* Core specs */}
          <div className='grid grid-cols-3 gap-3'>
            <div>
              <LookupFieldSelect
                label='Movement Type'
                value={form.movementType}
                lookupCollection='lookup_movement_types'
                onChange={(v) => updateForm({ movementType: v })}
              />
            </div>
            <div>
              <label className={labelClass}>Frequency (VPH)</label>
              <input type='number' value={form.frequencyVph} onChange={(e) => updateForm({ frequencyVph: e.target.value })} className={inputClass} placeholder='28800' />
            </div>
            <div>
              <label className={labelClass}>Jewels</label>
              <input type='number' value={form.jewels} onChange={(e) => updateForm({ jewels: e.target.value })} className={inputClass} placeholder='25' />
            </div>
            <div>
              <label className={labelClass}>Power Reserve (hours)</label>
              <input type='number' value={form.powerReserveHours} onChange={(e) => updateForm({ powerReserveHours: e.target.value })} className={inputClass} placeholder='42' />
            </div>
            <div>
              <label className={labelClass}>Positions Adjusted</label>
              <input type='number' value={form.positionsAdjusted} onChange={(e) => updateForm({ positionsAdjusted: e.target.value })} className={inputClass} placeholder='5' />
            </div>
            {/* Accuracy group — the two bounds are reinterpreted by the
                Symmetric flag (see formatAccuracy). Rendered as one grouped
                control spanning two grid cells. */}
            <div className='col-span-2'>
              <div className='flex items-center justify-between mb-1'>
                <label className={`${labelClass} mb-0`}>Accuracy (sec/day)</label>
                <label className='flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={form.accuracySymmetric}
                    onChange={(e) => updateForm({ accuracySymmetric: e.target.checked })}
                  />
                  Symmetric ± spec
                </label>
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className={labelClass}>
                    {form.accuracySymmetric ? 'Best-case spread' : 'Accuracy lower (sec/day)'}
                  </label>
                  <input type='number' step='any' value={form.accuracyLowerSecPerDay} onChange={(e) => updateForm({ accuracyLowerSecPerDay: e.target.value })} className={inputClass} placeholder={form.accuracySymmetric ? '10' : '-4'} />
                </div>
                <div>
                  <label className={labelClass}>
                    {form.accuracySymmetric ? 'Worst-case spread' : 'Accuracy upper (sec/day)'}
                  </label>
                  <input type='number' step='any' value={form.accuracyUpperSecPerDay} onChange={(e) => updateForm({ accuracyUpperSecPerDay: e.target.value })} className={inputClass} placeholder={form.accuracySymmetric ? '12' : '+6'} />
                </div>
              </div>
              <div className='text-xs text-gray-500 mt-1'>
                Preview:{' '}
                <span className='font-medium text-gray-700'>
                  {formatAccuracy(
                    form.accuracySymmetric,
                    form.accuracyLowerSecPerDay !== '' && !Number.isNaN(Number(form.accuracyLowerSecPerDay)) ? Number(form.accuracyLowerSecPerDay) : undefined,
                    form.accuracyUpperSecPerDay !== '' && !Number.isNaN(Number(form.accuracyUpperSecPerDay)) ? Number(form.accuracyUpperSecPerDay) : undefined,
                  )}
                </span>
              </div>
            </div>
            <div>
              <label className={labelClass}>Diameter (mm)</label>
              <input type='number' step='0.01' value={form.diameterMm} onChange={(e) => updateForm({ diameterMm: e.target.value })} className={inputClass} placeholder='25.60' />
            </div>
            <div>
              <label className={labelClass}>Height (mm)</label>
              <input type='number' step='0.01' value={form.heightMm} onChange={(e) => updateForm({ heightMm: e.target.value })} className={inputClass} placeholder='3.60' />
            </div>
            <div>
              <label className={labelClass}>Lift Angle (degrees)</label>
              <input type='number' value={form.liftAngleDegrees} onChange={(e) => updateForm({ liftAngleDegrees: e.target.value })} className={inputClass} placeholder='52' />
            </div>
            <div>
              <label className={labelClass}>Service Interval (years)</label>
              <input type='number' value={form.serviceIntervalYears} onChange={(e) => updateForm({ serviceIntervalYears: e.target.value })} className={inputClass} placeholder='5' />
            </div>
            <div>
              <label className={labelClass}>Casing Diameter (mm)</label>
              <input type='number' step='0.01' value={form.casingDiameterMm} onChange={(e) => updateForm({ casingDiameterMm: e.target.value })} className={inputClass} placeholder='26.00' />
            </div>
          </div>

          {/* Battery fields — shown only for battery-powered movement types */}
          {movementTypeNeedsBattery && (
            <div className='grid grid-cols-2 gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
              <div>
                <label className={labelClass}>Battery Type</label>
                <input type='text' value={form.batteryType} onChange={(e) => updateForm({ batteryType: e.target.value })} className={inputClass} placeholder='e.g., SR920SW, CR2025' />
              </div>
              <div>
                <label className={labelClass}>Battery Life (years)</label>
                <input type='number' step='0.5' min='0' max='20' value={form.batteryLifeYears} onChange={(e) => updateForm({ batteryLifeYears: e.target.value })} className={inputClass} placeholder='e.g., 2, 3.5, 10' />
              </div>
            </div>
          )}

          {/* Durability */}
          <div className='grid grid-cols-3 gap-3'>
            <div>
              <label className={labelClass}>Shock Resistance (G)</label>
              <input type='number' min='0' value={form.shockResistanceG} onChange={(e) => updateForm({ shockResistanceG: e.target.value })} className={inputClass} placeholder='e.g., 5000' />
            </div>
            <div>
              <label className={labelClass}>Anti-Magnetic (A/m)</label>
              <input type='number' min='0' value={form.antiMagneticAm} onChange={(e) => updateForm({ antiMagneticAm: e.target.value })} className={inputClass} placeholder='e.g., 4800' />
            </div>
            <div>
              <label className={labelClass}>Anti-Magnetic (Gauss)</label>
              <input type='number' min='0' value={form.antiMagneticGauss} onChange={(e) => updateForm({ antiMagneticGauss: e.target.value })} className={inputClass} placeholder='e.g., 60' />
            </div>
          </div>

          {/* String fields */}
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className={labelClass}>Base Movement</label>
              <div className='flex gap-1'>
                <input type='text' value={form.baseMovement} onChange={(e) => handleBaseMovementChange(e.target.value)} className={`${inputClass} flex-1`} placeholder='e.g., Sellita SW300-1' />
                {form.baseMovement?.trim() && (
                  <>
                    <button
                      type='button'
                      onClick={handleCheckBaseMovement}
                      disabled={baseMovementChecking}
                      className={`px-2 py-1 text-xs rounded border ${baseMovementInDb === true ? 'bg-green-50 border-green-300 text-green-700' : baseMovementInDb === false ? 'bg-red-50 border-red-300 text-red-700' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'}`}
                      title='Check if base movement exists in calibre database'
                    >
                      {baseMovementChecking ? '...' : baseMovementInDb === true ? '✓' : baseMovementInDb === false ? '✗' : 'Check'}
                    </button>
                    {baseMovementInDb === true && (
                      <button
                        type='button'
                        onClick={handleOpenBaseMovement}
                        className='px-2 py-1 text-xs rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                        title='Open base movement in editor'
                      >
                        Open
                      </button>
                    )}
                  </>
                )}
              </div>
              {/* Not found → offer inline actions to bootstrap the missing base
                  calibre. Reuses CalibreActionToolbar (AI Identify → enrich+create,
                  Create New → stub, Add as AKA → searchable brand-filtered picker).
                  On success we re-run the check so the badge flips to ✓. */}
              {form.baseMovement?.trim() && baseMovementInDb === false && (
                <div className='mt-1.5 flex items-center gap-2 flex-wrap'>
                  <span className='text-xs text-red-600'>Not in DB —</span>
                  <CalibreActionToolbar
                    calibreName={form.baseMovement}
                    brandName={brandById.get(form.brandId) || form.brandName}
                    brandId={form.brandId.trim() || undefined}
                    showAIIdentify={true}
                    showCreateNew={true}
                    onCalibreCreated={() => { void handleCheckBaseMovement(); }}
                    onAliasAdded={() => { void handleCheckBaseMovement(); }}
                    compact={true}
                  />
                </div>
              )}
            </div>
            <div>
              <LookupFieldSelect
                label='Winding Direction'
                value={form.windingDirection}
                lookupCollection='lookup_winding_directions'
                onChange={(v) => updateForm({ windingDirection: v })}
              />
            </div>
            <div>
              <LookupFieldSelect
                label='Anti-Shock System'
                value={form.antiShockSystem}
                lookupCollection='lookup_shock_protection'
                onChange={(v) => updateForm({ antiShockSystem: v })}
              />
            </div>
            <div>
              <label className={labelClass}>Rotor Type</label>
              <input type='text' value={form.rotorType} onChange={(e) => updateForm({ rotorType: e.target.value })} className={inputClass} placeholder='Ball-bearing, jewelled bearing, tungsten rotor…' />
            </div>
            <div>
              <LookupFieldSelect
                label='Regulator System'
                value={form.regulatorSystem}
                lookupCollection='lookup_regulation'
                onChange={(v) => updateForm({ regulatorSystem: v })}
              />
            </div>
            <div>
              <LookupFieldSelect
                label='Country of Manufacture'
                value={form.countryOfManufacture}
                lookupCollection='lookup_movement_origin'
                onChange={(v) => updateForm({ countryOfManufacture: v })}
              />
            </div>
            <div>
              <LookupFieldSelect
                label='Manual Winding Direction'
                value={form.manualWindingDirection}
                lookupCollection='lookup_winding_directions'
                onChange={(v) => updateForm({ manualWindingDirection: v })}
              />
            </div>
            <div>
              <LookupFieldSelect
                label='Hairspring'
                value={form.hairspring}
                lookupCollection='lookup_balance_spring'
                onChange={(v) => updateForm({ hairspring: v })}
              />
            </div>
            <div>
              <LookupFieldSelect
                label='Escapement'
                value={form.escapement}
                lookupCollection='lookup_escapement_type'
                onChange={(v) => updateForm({ escapement: v })}
              />
            </div>
            <div>
              <label className={labelClass}>Lignes</label>
              <input type='text' value={form.lignes} onChange={(e) => updateForm({ lignes: e.target.value })} className={inputClass} placeholder='e.g., 11.5' />
            </div>
            <div>
              <LookupFieldSelect
                label='Movement Architecture'
                value={form.movementArchitecture}
                lookupCollection='lookup_movement_architecture'
                onChange={(v) => updateForm({ movementArchitecture: v })}
              />
            </div>
            {/* Winding-system topology — automatic movements only. Rotor
                bearing/material detail belongs in Rotor Type above. */}
            {movementTypeIsAuto && (
              <div>
                <LookupFieldSelect
                  label='Auto Winding Architecture'
                  value={form.windingArchitecture}
                  lookupCollection='lookup_winding_architecture'
                  onChange={(v) => updateForm({ windingArchitecture: v })}
                />
              </div>
            )}
            {/* What the rotor is MADE OF (v1.92.0) — its topology is Auto
                Winding Architecture above, its bearing/suspension is the
                free-text Rotor Type. Gated on automatic for the same reason
                and with the same behaviour as the field above it: a value set
                before the movement type flipped is kept, just no longer
                rendered. */}
            {movementTypeIsAuto && (
              <div>
                <LookupFieldSelect
                  label='Rotor Material'
                  value={form.rotorMaterial}
                  lookupCollection='lookup_rotor_material'
                  onChange={(v) => updateForm({ rotorMaterial: v })}
                />
              </div>
            )}
            <div>
              <LookupFieldSelect
                label='Bridge Material'
                value={form.bridgeMaterial}
                lookupCollection='lookup_bridge_material'
                onChange={(v) => updateForm({ bridgeMaterial: v })}
              />
            </div>
            <div>
              <LookupFieldSelect
                label='Movement Finishing'
                value={form.movementFinishing}
                lookupCollection='lookup_movement_finishing'
                onChange={(v) => updateForm({ movementFinishing: v })}
              />
            </div>
            {/* The mainplate's ONE defining surface treatment (v1.92.0) —
                narrower than Movement Finishing beside it, which grades the
                movement's overall finishing quality. Every other technique
                belongs in Decorations below. */}
            <div>
              <LookupFieldSelect
                label='Plate Finishing'
                value={form.plateFinishing}
                lookupCollection='lookup_plate_finishing'
                onChange={(v) => updateForm({ plateFinishing: v })}
              />
            </div>
            <div>
              <label className={labelClass}>Skeletonization</label>
              <select
                value={form.skeletonization}
                onChange={(e) => updateForm({ skeletonization: e.target.value as '' | 'full' | 'partial' | 'none' })}
                className={inputClass}
              >
                <option value=''>— unset —</option>
                <option value='none'>None (solid)</option>
                <option value='partial'>Partial (open-heart)</option>
                <option value='full'>Full (skeletonised)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Number of Bridges</label>
              <input type='number' value={form.numberOfBridges} onChange={(e) => updateForm({ numberOfBridges: e.target.value })} className={inputClass} placeholder='e.g., 3' />
            </div>
            <div>
              <label className={labelClass}>Barrel Count</label>
              <input type='number' value={form.barrelCount} onChange={(e) => updateForm({ barrelCount: e.target.value })} className={inputClass} placeholder='e.g., 1' />
            </div>
          </div>

          {/* Balance Type — multi-select since v1.91.0. The slugs span three
              orthogonal axes (material / construction / regulation architecture),
              so a well-specified balance carries several at once. Sits outside
              the specs grid with Decorations: a chip picker needs the full width
              a grid column doesn't give it. */}
          <div>
            <LookupMultiSelect
              label='Balance Type'
              values={form.balanceType}
              lookupCollection='lookup_balance_type'
              onChange={(v) => updateForm({ balanceType: v })}
            />
          </div>

          {/* Decorations — curated multi-select (perlage, Côtes de Genève, …). */}
          <div>
            <LookupMultiSelect
              label='Decorations'
              values={form.decorations}
              lookupCollection='lookup_movement_decoration'
              onChange={(v) => updateForm({ decorations: v })}
            />
          </div>

          {/* Case materials this calibre is OFFERED in (v1.92.0) — availability,
              not composition: it constrains which references can legitimately
              carry the calibre. Shares lookup_case_materials with the
              reference-level case material, whose config is isMultiSelect:false
              for that single-select field; the multi-ness here is this control,
              not the flag. Leave empty when the calibre is unrestricted rather
              than listing every material. */}
          <div>
            <LookupMultiSelect
              label='Case Materials Available'
              values={form.caseMaterialsAvailable}
              lookupCollection='lookup_case_materials'
              onChange={(v) => updateForm({ caseMaterialsAvailable: v })}
            />
          </div>

          {/* Booleans */}
          <div className='flex flex-wrap gap-4'>
            {([
              ['hacking', 'Hacking'],
              ['manualWinding', 'Manual Winding'],
              ['quickSetDate', 'Quick Set Date'],
              ['isCosc', 'COSC Certified'],
              ['isGlashuetteRegulation', 'Glashütte Regulation'],
              ['isChronometer', 'Chronometer'],
              ['isGenevaSeal', 'Geneva Seal'],
              ['isGrandSeikoStandard', 'Grand Seiko Standard'],
              ['isMETAS', 'METAS Certified'],
              ['isGMT', 'GMT'],
              ['isChronograph', 'Chronograph'],
              ['isInHouse', 'In-House'],
              ['columnWheel', 'Column Wheel'],
              ['doubleBarrel', 'Double Barrel'],
              ['rhodiumPlated', 'Rhodium Plated'],
              ['giltFinish', 'Gilt Finish'],
            ] as const).map(([key, label]) => (
              <label key={key} className='flex items-center gap-2 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={form[key] as boolean}
                  onChange={(e) => updateForm({ [key]: e.target.checked })}
                  className='rounded text-brand-600 focus:ring-brand-500'
                />
                <span className='text-sm text-gray-700'>{label}</span>
              </label>
            ))}
          </div>

          {/* Movement-family flag — rendered separately from the chip row so it
              can carry hint text explaining when to mark a calibre as a
              family-level catch-all rather than a specific reference. */}
          <div>
            <label className='flex items-center gap-2 cursor-pointer'>
              <input
                type='checkbox'
                checked={form.isFamily}
                onChange={(e) => updateForm({ isFamily: e.target.checked })}
                className='rounded text-brand-600 focus:ring-brand-500'
              />
              <span className='text-sm text-gray-700'>Movement family (catch-all, not a specific calibre)</span>
            </label>
            <p className='text-xs text-gray-400 mt-1 ml-6'>
              Use for vague historical movement designations like 'Felsa Bidynator' or 'Glashütte Spezimatic' where the specific calibre isn't known.
            </p>
          </div>

          {/* Catch-all flag — excludes this calibre from cross-manufacturer
              brand-derivation logic. Distinct from Movement family: a family is
              a real movement that SHOULD derive brand associations; a catch-all
              is a junk bucket for generic strings that must NOT. */}
          <div>
            <label className='flex items-center gap-2 cursor-pointer'>
              <input
                type='checkbox'
                checked={form.isCatchAll}
                onChange={(e) => updateForm({ isCatchAll: e.target.checked })}
                className='rounded text-brand-600 focus:ring-brand-500'
              />
              <span className='text-sm text-gray-700'>Catch-all placeholder (exclude from brand derivation)</span>
            </label>
            <p className='text-xs text-gray-400 mt-1 ml-6'>
              Use for generic buckets like 'Unspecified Quartz' or 'Automatic'. References resolving here won't contribute to any manufacturer's derived brand set.
            </p>
          </div>

          {/* Arrays */}
          <div className='grid grid-cols-2 gap-3'>
            <TagInput label='Date' values={form.date} onChange={(v) => updateForm({ date: v })} placeholder='Add date type...' />
            <TagInput label='Hands' values={form.hands} onChange={(v) => updateForm({ hands: v })} placeholder='Add hand type...' />
            <TagInput label='Chronograph' values={form.chronograph} onChange={(v) => updateForm({ chronograph: v })} placeholder='Add chronograph type...' />
            <TagInput label='Additionals' values={form.additionals} onChange={(v) => updateForm({ additionals: v })} placeholder='Add additional feature...' />
            <TagInput label='Astronomical' values={form.astronomical} onChange={(v) => updateForm({ astronomical: v })} placeholder='Add astronomical complication...' />
            <TagInput label='Acoustic' values={form.acoustic} onChange={(v) => updateForm({ acoustic: v })} placeholder='Add acoustic feature...' />
          </div>

          {/* Tiers — variant grades of this calibre (base value shown per row is
              derived from the live form so it reflects unsaved edits). */}
          <CalibreTiersEditor
            tiers={tiers}
            onChange={setTiers}
            baseData={formToData(form)}
            baseTierName={form.baseTierName}
            onBaseTierNameChange={(name) => updateForm({ baseTierName: name })}
            baseSpec={formToData(form)}
            tierVocabulary={tierVocabulary}
            onAddTierName={tierManufacturerId ? handleAddTierName : undefined}
            brandOptions={tierBrandOptions}
            defaultBrandId={form.brandId || undefined}
            media={tierMedia}
          />

          {/* Images */}
          <div className='border rounded-lg p-3 bg-gray-50'>
            <div className='flex items-center justify-between mb-2'>
              <label className='text-xs font-medium text-gray-600 uppercase'>Images ({images.length}/{MAX_IMAGES})</label>
            </div>
            {images.length > 0 && (
              <div className='flex flex-wrap gap-3 mb-3'>
                {images.map((img, idx) => {
                  const displaySrc = img.storagePath
                    ? resolvedUrls[img.storagePath] || ''
                    : img.url || '';
                  return (
                    <div key={idx} className='relative group border rounded-lg overflow-hidden bg-white' style={{ width: 120 }}>
                      {displaySrc ? (
                        <img src={displaySrc} alt={img.caption || `Image ${idx + 1}`} className='w-full h-24 object-cover' onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }} />
                      ) : (
                        <div className='w-full h-24 bg-gray-100 flex items-center justify-center text-[10px] text-gray-400'>Loading…</div>
                      )}
                      <div className='p-1.5 text-[10px] text-gray-600'>
                        <label className='block text-[9px] font-medium text-gray-400 uppercase mb-0.5'>Source</label>
                        <select
                          value={img.source || defaultCalibreSource}
                          onChange={(e) => handleUpdateImageSource(idx, e.target.value)}
                          className='w-full border border-gray-200 rounded px-1 py-0.5 text-[10px] bg-white mb-1'
                          title='Image attribution source'
                        >
                          {sourceOptionsFor('image', img.source).map((s) => (
                            <option key={s} value={s}>{labelForCalibreSource(s)}</option>
                          ))}
                        </select>
                        {img.caption && <div className='truncate text-gray-500' title={img.caption}>{img.caption}</div>}
                      </div>
                      <button
                        type='button'
                        onClick={() => handleRemoveImage(idx)}
                        className='absolute top-1 right-1 bg-white border rounded-full text-red-500 hover:text-red-700 hover:bg-red-50 w-6 h-6 flex items-center justify-center shadow-sm'
                        title='Remove image'
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {images.length < MAX_IMAGES && (
              <div className='space-y-2'>
                <div className='flex gap-2'>
                  <input
                    type='url'
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder='Image URL...'
                    className={`${inputClass} flex-1`}
                  />
                  <input
                    type='text'
                    value={newImageCaption}
                    onChange={(e) => setNewImageCaption(e.target.value)}
                    placeholder='Caption (optional)'
                    className={`${inputClass} flex-1`}
                  />
                  <Button variant='secondary' size='sm' onClick={handleAddImageFromUrl} disabled={uploadingImage}>
                    {uploadingImage ? 'Uploading…' : 'Add from URL'}
                  </Button>
                </div>
                <div className='flex items-center gap-2'>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='image/*'
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleAddImageFromFile(file);
                    }}
                    disabled={uploadingImage}
                    className='text-xs text-gray-600'
                  />
                  <span className='text-xs text-gray-400'>or upload from your computer</span>
                </div>
              </div>
            )}
          </div>

          {/* Source info */}
          <div className='space-y-2'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Source</label>
              <select
                value={editingSource || defaultCalibreSource}
                onChange={(e) => setEditingSource(e.target.value)}
                className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                title='Entity-level attribution source'
              >
                {sourceOptionsFor('calibre', editingSource).map((s) => (
                  <option key={s} value={s}>{labelForCalibreSource(s)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Source URL</label>
              <input
                type='url'
                value={editingSourceUrl || ''}
                onChange={(e) => setEditingSourceUrl(e.target.value || undefined)}
                placeholder='https://calibercorner.com/...'
                className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
              />
            </div>
          </div>

          {/* Actions */}
          <div className='flex items-center justify-end gap-2 pt-2 border-t'>
            {/* Reclassify: only for an existing, non-deprecated calibre.
                Deprecated calibres offer "Reclassify again" instead. */}
            {editingId && editingEntry && !editingEntry.deprecated && (
              <button
                type='button'
                onClick={() => setReclassifyOpen(true)}
                className='mr-auto text-xs font-medium text-amber-700 hover:text-amber-900 underline'
                title='Reclassify this standalone calibre as a brand-specific tier of another calibre'
              >
                Reclassify as brand-specific tier…
              </button>
            )}
            {editingId && editingEntry?.deprecated && (
              <button
                type='button'
                onClick={() => setReclassifyOpen(true)}
                className='mr-auto text-xs font-medium text-amber-700 hover:text-amber-900 underline'
              >
                Reclassify again (wrong target)…
              </button>
            )}
            {/* Existing calibres only — a new one has nothing to match yet. */}
            {editingId && editingEntry && (reapplyBlocked || formHasUnsavedEdits) && (
              <span className='text-xs text-gray-500'>
                {reapplyBlocked ?? 'Save your changes to reapply.'}
              </span>
            )}
            {editingId && editingEntry && (
              <Button
                variant='secondary'
                size='sm'
                onClick={() => setReapplyOpen(true)}
                disabled={saving || formHasUnsavedEdits || !!reapplyBlocked}
                title={
                  reapplyBlocked ??
                  (formHasUnsavedEdits
                    ? 'Save or discard your changes first — the calibre is applied as last saved.'
                    : "Find this calibre's references in every brand and push its data onto them")
                }
              >
                Reapply to matching references…
              </Button>
            )}
            <Button variant='secondary' size='sm' onClick={handleCloseForm}>
              Cancel
            </Button>
            {/* Deprecated calibres save too. The edit path updates the doc in
                place (forceOverwriteId = self), so `deprecated` and the
                redirectTo* fields survive — and cleanup on a soft-deleted alias
                (an incomplete Reclassify migration, a stray AKA, a wrong name)
                has nowhere else to happen. */}
            <Button variant='primary' size='sm' onClick={() => handleSave()} disabled={saving || !form.name.trim()}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
            </Button>
          </div>
        </div>

        {editingEntry && (
          <ReclassifyCalibreWizard
            isOpen={reclassifyOpen}
            source={editingEntry}
            brandOptions={brandOptions}
            onClose={() => setReclassifyOpen(false)}
            onComplete={(result) => {
              setReclassifyOpen(false);
              toast.success(
                `Reclassified: ${result.refsRepointed} ref(s) re-pointed, source deprecated.`,
              );
              // The source is now a redirect alias — leave the form and refresh.
              onSaved?.(editingEntry.id);
              handleCloseForm();
            }}
          />
        )}
        {editingEntry && reapplyOpen && (
          <ReapplyCalibreToReferences
            isOpen
            onClose={() => setReapplyOpen(false)}
            calibreId={editingEntry.id}
            calibreName={editingEntry.name}
          />
        )}
        <MovementManufacturerQuickCreate
          isOpen={showCreateMfr}
          onCancel={() => setShowCreateMfr(false)}
          onCreated={handleMfrCreated}
        />

        {/* AI apply review — per-field current-vs-proposed picker. Lets the user
            override existing values (the fill-empty accept couldn't). */}
        <Modal
          isOpen={aiApplyOpen}
          onClose={() => setAiApplyOpen(false)}
          title='Apply AI suggestions'
          size='lg'
        >
          <div className='space-y-3'>
            <div className='flex items-center gap-2 text-sm text-gray-600'>
              <span>Tick the fields to apply, then confirm. Applying overwrites the current value.</span>
              {aiState.result?.confidence && (
                <span className='ml-auto px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800'>
                  confidence: {aiState.result.confidence}
                </span>
              )}
            </div>

            <div className='flex items-center gap-3 text-xs'>
              <button
                type='button'
                onClick={() =>
                  setAiFieldSel(Object.fromEntries(aiFieldDiffs.map((d) => [d.key, true])))
                }
                className='text-brand-600 hover:text-brand-800 underline'
              >
                Select all
              </button>
              <button
                type='button'
                onClick={() =>
                  setAiFieldSel(Object.fromEntries(aiFieldDiffs.map((d) => [d.key, false])))
                }
                className='text-gray-500 hover:text-gray-700 underline'
              >
                Select none
              </button>
              <button
                type='button'
                onClick={() =>
                  setAiFieldSel(
                    Object.fromEntries(aiFieldDiffs.map((d) => [d.key, d.changed])),
                  )
                }
                className='text-gray-500 hover:text-gray-700 underline'
                title='Tick only fields whose proposed value differs from the current one'
              >
                Changed only
              </button>
            </div>

            <div className='border border-gray-200 rounded-lg overflow-hidden max-h-[26rem] overflow-y-auto'>
              <table className='w-full text-sm'>
                <thead className='sticky top-0 bg-gray-50 text-gray-500'>
                  <tr>
                    <th className='w-10 px-3 py-2' />
                    <th className='text-left font-medium px-3 py-2'>Field</th>
                    <th className='text-left font-medium px-3 py-2'>Current</th>
                    <th className='text-left font-medium px-3 py-2'>AI proposed</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {aiFieldDiffs.map((d) => (
                    <tr key={d.key} className={d.changed ? undefined : 'opacity-60'}>
                      <td className='px-3 py-2 align-top'>
                        <input
                          type='checkbox'
                          checked={!!aiFieldSel[d.key]}
                          onChange={(e) =>
                            setAiFieldSel((prev) => ({ ...prev, [d.key]: e.target.checked }))
                          }
                          className='rounded border-gray-300 text-brand-600 focus:ring-brand-500 mt-0.5'
                        />
                      </td>
                      <td className='px-3 py-2 align-top text-gray-800'>
                        {d.label}
                        {!d.changed && (
                          <span className='ml-1 text-[10px] text-gray-400'>(same)</span>
                        )}
                      </td>
                      <td className='px-3 py-2 align-top font-mono text-gray-500 break-words max-w-[14rem]'>
                        {d.current}
                      </td>
                      <td
                        className={`px-3 py-2 align-top font-mono break-words max-w-[14rem] ${
                          d.changed ? 'text-emerald-700' : 'text-gray-500'
                        }`}
                      >
                        {d.proposed}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* AI-proposed tier variants. A proposal whose name already exists
                is disabled (the append-only merge never clobbers a curated
                tier); one that names a grade the calibre already has under a
                different name can be REMAPPED onto it instead. Accepted tiers
                get a fresh id and are added to tiers[]; remapped ones merge
                their overrides into the chosen tier. The user still confirms
                with Save.

                The row is a div, not a label: it contains a select, and a label
                wrapping an interactive control hands every click on that control
                to the checkbox. The tier name carries the htmlFor instead. */}
            {aiProposedTiers.length > 0 && (
              <div className='space-y-2'>
                <div className='text-xs font-medium text-gray-500 uppercase'>
                  Proposed tier variants ({aiProposedTiers.length})
                </div>
                <div className='space-y-1.5'>
                  {aiProposedTiers.map(({ tier, exists, overrideCount, matchedTier, mergeRows }, i) => {
                    const mode = aiTierMode[i] ?? 'merge';
                    const ticked = aiTierMergeFields[i] ?? new Set<string>();
                    const setTicked = (next: Set<string>) =>
                      setAiTierMergeFields((prev) => ({ ...prev, [i]: next }));
                    return (
                    <div
                      key={`${tier.name}-${i}`}
                      className={`flex items-start gap-2 border rounded-lg px-3 py-2 ${
                        exists && !aiTierSel[i] ? 'bg-gray-50' : 'bg-white'
                      }`}
                    >
                      <input
                        type='checkbox'
                        id={`ai-tier-${i}`}
                        checked={!!aiTierSel[i]}
                        onChange={(e) =>
                          setAiTierSel((prev) => ({ ...prev, [i]: e.target.checked }))
                        }
                        className='rounded border-gray-300 text-brand-600 focus:ring-brand-500 mt-0.5'
                      />
                      <div className='min-w-0 flex-1'>
                        <div className='flex items-center gap-2'>
                          <label
                            htmlFor={`ai-tier-${i}`}
                            className='text-sm font-medium text-gray-800 cursor-pointer'
                          >
                            {tier.name}
                          </label>
                          <span className='text-[11px] text-gray-500 bg-gray-100 rounded-full px-2 py-0.5'>
                            {overrideCount} override{overrideCount === 1 ? '' : 's'}
                          </span>
                          {exists && (
                            <span className='text-[11px] text-amber-600'>
                              {aiTierSel[i]
                                ? 'already exists on this calibre'
                                : 'already exists — skipped'}
                            </span>
                          )}
                        </div>
                        {tier.description && (
                          <div className='text-xs text-gray-500'>{tier.description}</div>
                        )}
                        {overrideCount > 0 && (
                          <div className='text-[11px] text-gray-400 font-mono break-words'>
                            {Object.entries(tier.overrides)
                              .filter(([, v]) => v !== undefined && v !== null && v !== '')
                              .map(([k, v]) => `${k}=${String(v)}`)
                              .join(', ')}
                          </div>
                        )}
                        {/*
                          Sibling-calibre backstop. The enrichCalibre model has
                          been observed proposing calibres from the same family
                          (Unitas 6498-2 under 6498-1, Valjoux 7753 under 7750)
                          as if they were tier variants of the base. A tier
                          shares the base's mechanical architecture; a sibling
                          differs on beat rate, escapement, hairspring, balance
                          type or complication layout, and belongs in its own
                          calibre document — not in this base's tiers[].

                          Two triggers, either one is enough (see
                          calibreSiblingSignals for the reasoning): overrides
                          that touch two or more mechanical signals, OR a
                          name that carries a calibre-generation suffix like
                          `-2` or ` III`. Non-blocking on purpose — the
                          curator can still accept the tier if they know
                          better.
                        */}
                        {(() => {
                          const verdict = siblingWarningVerdict(tier);
                          if (!verdict.showWarning) return null;
                          const signals = verdict.differingSignals.map(
                            (k) => SIBLING_SIGNAL_LABELS[k],
                          );
                          const reasonParts: string[] = [];
                          if (signals.length >= 2) {
                            reasonParts.push(
                              `differs on ${signals.length} mechanical signals (${signals.join(', ')})`,
                            );
                          } else if (signals.length === 1) {
                            reasonParts.push(
                              `differs on ${signals[0]}`,
                            );
                          }
                          if (verdict.nameLooksLikeGeneration) {
                            reasonParts.push(
                              'the name looks like a calibre-generation suffix',
                            );
                          }
                          return (
                            <div
                              role='alert'
                              className='mt-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800'
                            >
                              <span className='font-medium'>
                                Looks like a sibling calibre, not a tier —{' '}
                              </span>
                              this proposal {reasonParts.join(', and ')}. This
                              usually indicates a separate calibre in the same
                              family. Consider creating it as its own calibre
                              instead of accepting it here.
                            </div>
                          );
                        })()}
                        {/* Merge: the proposal names a tier the calibre
                            already has. Skipping it (unticked) stays the
                            default; ticking opens a per-field picker whose
                            ticked fields OVERWRITE that tier. This is the one
                            place the model's answer beats a curated value, and
                            only because the curator ticked it field by field.
                            'Remap to another tier' is the escape hatch for
                            "right values, wrong tier". */}
                        {matchedTier && aiTierSel[i] && (
                          <div className='mt-1.5 space-y-1.5'>
                            <div className='flex items-center gap-1.5'>
                              <span className='text-[11px] text-gray-500'>Action:</span>
                              <select
                                value={mode}
                                aria-label={`Action for proposed tier ${tier.name}`}
                                onChange={(e) =>
                                  setAiTierMode((prev) => ({
                                    ...prev,
                                    [i]: e.target.value as 'merge' | 'remap',
                                  }))
                                }
                                className='border border-gray-300 rounded px-1.5 py-0.5 text-[11px]'
                              >
                                <option value='merge'>
                                  Merge selected fields into “{matchedTier.name}”
                                </option>
                                <option value='remap'>Remap to another tier</option>
                              </select>
                            </div>
                            {mode === 'merge' && mergeRows.length === 0 && (
                              <div className='text-[11px] text-gray-500'>
                                Nothing to merge — this proposal carries no value
                                that differs from “{matchedTier.name}”.
                              </div>
                            )}
                            {mode === 'merge' && mergeRows.length > 0 && (
                              <div className='rounded border border-gray-200 bg-gray-50 p-2 space-y-1'>
                                <div className='flex items-center justify-between'>
                                  <span className='text-[11px] font-medium text-gray-600'>
                                    Fields to overlay
                                  </span>
                                  <div className='flex gap-2'>
                                    <button
                                      type='button'
                                      className='text-[11px] text-brand-600 hover:underline'
                                      onClick={() =>
                                        setTicked(new Set(mergeRows.map((r) => r.key)))
                                      }
                                    >
                                      All
                                    </button>
                                    <button
                                      type='button'
                                      className='text-[11px] text-brand-600 hover:underline'
                                      onClick={() => setTicked(new Set())}
                                    >
                                      None
                                    </button>
                                  </div>
                                </div>
                                {mergeRows.map((row) => {
                                  const on = ticked.has(row.key);
                                  return (
                                    <label
                                      key={row.key}
                                      className='flex items-start gap-2 text-[11px] cursor-pointer'
                                    >
                                      <input
                                        type='checkbox'
                                        checked={on}
                                        onChange={(e) => {
                                          const next = new Set(ticked);
                                          if (e.target.checked) next.add(row.key);
                                          else next.delete(row.key);
                                          setTicked(next);
                                        }}
                                        className='rounded border-gray-300 text-brand-600 focus:ring-brand-500 mt-0.5'
                                      />
                                      <span className='w-32 shrink-0 text-gray-600'>
                                        {row.label}
                                      </span>
                                      <span className='min-w-0 flex-1 break-words'>
                                        <span
                                          className={
                                            on
                                              ? 'text-gray-500 line-through'
                                              : 'text-gray-500'
                                          }
                                        >
                                          {row.existing}
                                        </span>
                                        <span className='mx-1 text-gray-400'>→</span>
                                        <span className='font-medium text-gray-800'>
                                          {row.proposed}
                                        </span>
                                      </span>
                                    </label>
                                  );
                                })}
                                <div className='text-[11px] text-gray-500 pt-0.5'>
                                  {ticked.size === 0
                                    ? 'No fields selected — this tier will be left alone.'
                                    : `${ticked.size} field${
                                        ticked.size === 1 ? '' : 's'
                                      } will overwrite “${matchedTier.name}” on Save.`}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Remap: the model often proposes the right grade under
                            a name the calibre already has in another form
                            ("RR1102-C" for an existing "C"). Mapping merges its
                            overrides into that tier FILL-EMPTY instead of
                            creating a near-duplicate. Offered for a proposal
                            with no name match, and for a matched one whose
                            action is set to 'remap'. */}
                        {(!exists || (!!aiTierSel[i] && mode === 'remap')) &&
                          tiers.length > 0 && (
                          <div className='mt-1 flex items-center gap-1.5'>
                            <span className='text-[11px] text-gray-500'>Map to:</span>
                            <select
                              value={aiTierRemap[i] ?? ''}
                              aria-label={`Map proposed tier ${tier.name} to an existing tier`}
                              onChange={(e) =>
                                setAiTierRemap((prev) => ({
                                  ...prev,
                                  [i]: e.target.value,
                                }))
                              }
                              className='border border-gray-300 rounded px-1.5 py-0.5 text-[11px]'
                            >
                              <option value=''>none — create new</option>
                              {tiers.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                            {aiTierRemap[i] && (
                              <span className='text-[11px] text-emerald-700'>
                                merges into this tier; values it already sets are
                                kept
                              </span>
                            )}
                          </div>
                        )}
                        {/* Rename to the listed spelling: offered when the
                            proposal is mapped onto the existing tier that spells
                            the same listed grade another way. */}
                        {aiProposedTiers[i].vocabMatch &&
                          aiTierRemap[i] === aiProposedTiers[i].vocabMatch?.tier.id && (
                          <label className='mt-1 flex items-center gap-1.5 text-[11px] text-gray-700'>
                            <input
                              type='checkbox'
                              checked={!!aiTierRename[i]}
                              onChange={(e) =>
                                setAiTierRename((prev) => {
                                  const next = { ...prev };
                                  if (e.target.checked && aiProposedTiers[i].vocabMatch) next[i] = aiProposedTiers[i].vocabMatch!.to;
                                  else delete next[i];
                                  return next;
                                })
                              }
                              className='rounded border-gray-300 text-brand-600 focus:ring-brand-500'
                            />
                            Rename “{aiProposedTiers[i].vocabMatch?.tier.name}” to the listed name “
                            {aiProposedTiers[i].vocabMatch?.to}”
                          </label>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className='flex justify-end gap-2 pt-2 border-t'>
              <Button variant='ghost' size='sm' onClick={() => setAiApplyOpen(false)}>
                Cancel
              </Button>
              <Button
                variant='secondary'
                size='sm'
                onClick={() =>
                  applyAiFields(
                    new Set(aiFieldDiffs.map((d) => d.key)),
                    new Set(
                      aiProposedTiers.flatMap(({ exists }, i) => (exists ? [] : [i])),
                    ),
                  )
                }
              >
                Apply all
              </Button>
              <Button
                variant='primary'
                size='sm'
                disabled={aiSelectedCount === 0 && aiSelectedTierCount === 0}
                onClick={() =>
                  applyAiFields(
                    new Set(aiFieldDiffs.filter((d) => aiFieldSel[d.key]).map((d) => d.key)),
                    new Set(
                      aiProposedTiers.flatMap((_, i) => (aiTierSel[i] ? [i] : [])),
                    ),
                  )
                }
              >
                Apply selected ({aiSelectedCount + aiSelectedTierCount})
              </Button>
            </div>
          </div>
        </Modal>

        {/* AKA/name collision warning — warn-only, no merge/delete. The save is
            aborted until the user picks. "Save anyway" re-runs the save skipping
            the check; "Go back" leaves the editor open with nothing changed. */}
        <Modal
          isOpen={!!conflictWarning}
          onClose={() => setConflictWarning(null)}
          title='Duplicate AKA detected'
          size='md'
        >
          {conflictWarning && (
            <div className='space-y-4'>
              <p className='text-sm text-gray-700'>
                The AKA{' '}
                <span className='font-semibold'>“{conflictWarning.akaName}”</span>{' '}
                also exists on another calibre:{' '}
                <span className='font-semibold'>{conflictWarning.otherCalibreName}</span>{' '}
                <span className='text-gray-500'>(id: {conflictWarning.otherCalibreId})</span>.
              </p>
              <p className='text-sm text-gray-600'>
                To avoid duplicate AKAs, consider consolidating manually — but this
                save will proceed with the current AKA list unchanged.
              </p>
              <div className='flex justify-end gap-2 pt-2 border-t'>
                <Button
                  variant='secondary'
                  size='sm'
                  onClick={() => setConflictWarning(null)}
                >
                  Go back
                </Button>
                <Button
                  variant='primary'
                  size='sm'
                  onClick={() => {
                    setConflictWarning(null);
                    void handleSave(true);
                  }}
                >
                  Save anyway
                </Button>
              </div>
            </div>
          )}
        </Modal>
        <Modal
          isOpen={!!renameWarning}
          onClose={() => setRenameWarning(null)}
          title='Rename calibre?'
          size='md'
        >
          {renameWarning && (
            <div className='space-y-4'>
              <p className='text-sm text-gray-700'>
                This calibre has {renameWarning.refCount}{' '}
                {renameWarning.refCount === 1 ? 'reference' : 'references'} attached. Renaming it to{' '}
                <span className='font-semibold'>‘{renameWarning.newName}’</span> will keep the references linked
                but their displayed calibre name will change. Proceed?
              </p>
              <div className='flex justify-end gap-2 pt-2 border-t'>
                <Button variant='secondary' size='sm' onClick={() => setRenameWarning(null)}>
                  Cancel
                </Button>
                <Button
                  variant='primary'
                  size='sm'
                  onClick={() => {
                    setRenameWarning(null);
                    // The conflict check already passed to reach this dialog.
                    void handleSave(true, true);
                  }}
                >
                  Rename
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title='Custom Calibres' size='lg'>
      <p className='text-sm text-gray-500 mb-3'>
        Custom calibres are checked first during lookup. Use for in-house movements or calibres not found online.
      </p>

      <div className='flex items-center gap-2 mb-3'>
        <Button variant='primary' size='sm' onClick={handleAdd}>
          Add Custom Calibre
        </Button>
        <input
          type='text'
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          placeholder='Search calibres...'
          className='flex-1 border rounded-lg px-3 py-1.5 text-sm'
        />
        <label className='flex items-center gap-1 text-xs text-gray-500 cursor-pointer whitespace-nowrap'>
          <input type='checkbox' checked={showAllBrands} onChange={(e) => { setShowAllBrands(e.target.checked); setPage(0); }} className='rounded' />
          All brands
        </label>
      </div>

      {(() => {
        let filtered = showAllBrands
          ? entries
          : entries.filter((e) => {
              // Brand-scope filter: prefer canonical brandId; fall back to legacy brandName.
              if (e.brandId) return !!defaultBrandId && e.brandId === defaultBrandId;
              if (e.brandName) return !!currentBrandName && e.brandName.toLowerCase() === currentBrandName.toLowerCase();
              return true; // truly global (no brandId, no brandName)
            });
        const hiddenCount = entries.length - filtered.length;

        // Apply search
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          filtered = filtered.filter((e) =>
            e.name.toLowerCase().includes(q) ||
            (e.brandName || '').toLowerCase().includes(q) ||
            (e.data.movementType || '').toLowerCase().includes(q)
          );
        }

        // Paging
        const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
        const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

        if (loading) {
          return <div className='text-center py-8 text-gray-500 text-sm'>Loading...</div>;
        }
        if (filtered.length === 0) {
          return <div className='text-center py-8 text-gray-400 text-sm'>
            {entries.length > 0
              ? `No calibres for ${currentBrandName || 'this brand'} (${entries.length} other brand calibres hidden)`
              : 'No custom calibres yet.'}
          </div>;
        }
        return (
          <>
            <div className='border rounded-lg overflow-hidden max-h-80 overflow-y-auto'>
              <table className='w-full text-sm'>
                <thead className='sticky top-0'>
                  <tr className='bg-gray-50 border-b'>
                    <th className='text-left px-3 py-2 font-medium text-gray-600'>Name</th>
                    <th className='text-left px-3 py-2 font-medium text-gray-600'>Brand</th>
                    <th className='text-left px-3 py-2 font-medium text-gray-600'>Type</th>
                    <th className='text-left px-3 py-2 font-medium text-gray-600'>Summary</th>
                    <th className='w-20' />
                  </tr>
                </thead>
                <tbody>
                  {paged.map((entry) => {
                    const d = entry.data;
                    const summary = [
                      d.jewels && `${d.jewels}j`,
                      d.frequencyVph && `${(d.frequencyVph / 1000).toFixed(1)}k vph`,
                      d.powerReserveHours && `${d.powerReserveHours}h PR`,
                    ].filter(Boolean).join(', ');
                    return (
                      <tr key={entry.id} className='border-b last:border-b-0 hover:bg-gray-50'>
                        <td className='px-3 py-2 font-medium text-gray-900'>{entry.name}</td>
                        <td className='px-3 py-2 text-gray-500'>
                          <CalibreBrandCell entry={entry} brandById={brandById} />
                        </td>
                        <td className='px-3 py-2 text-gray-600 capitalize'>{d.movementType || '—'}</td>
                        <td className='px-3 py-2 text-gray-500 text-xs'>{summary || '—'}</td>
                        <td className='px-2 py-2 flex gap-1 justify-end'>
                          <button
                            type='button'
                            onClick={() => handleEdit(entry)}
                            className='text-blue-400 hover:text-blue-600 transition-colors'
                            title='Edit'
                          >
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                            </svg>
                          </button>
                          <button
                            type='button'
                            onClick={() => handleDelete(entry)}
                            disabled={deletingId === entry.id}
                            className='text-red-400 hover:text-red-600 transition-colors disabled:opacity-50'
                            title='Delete'
                          >
                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className='flex items-center justify-between mt-3'>
              <div className='text-xs text-gray-400'>
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
                {!showAllBrands && hiddenCount > 0 && ` (${hiddenCount} other brands hidden)`}
              </div>
              {totalPages > 1 && (
                <div className='flex items-center gap-1'>
                  <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className='px-2 py-1 text-xs border rounded disabled:opacity-30'>←</button>
                  <span className='text-xs text-gray-500'>Page {page + 1}/{totalPages}</span>
                  <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} className='px-2 py-1 text-xs border rounded disabled:opacity-30'>→</button>
                </div>
              )}
            </div>
          </>
        );
      })()}

    </Modal>
  );
}
